/**
 * FigureModal — 그림 하나를 다루는 창. 동작 넷이 목록·순번 대조·낙관락·오류 문구를 전부
 * 공유하므로 껍데기 하나에 담는다.
 *
 * ── 2026-08-31: 앞 셋은 **같은 창**이다 (오너 지시 「하나로 통일」) ──
 * 종전에는 `action` 이 창의 얼굴까지 갈랐다 — 편집만 3단(원본·미리보기·숫자)이고 나머지는 2단,
 * 버튼도 자리마다 달랐다. 그 가름이 **저장을 잠그는 버그**를 낳았다: [그림 교체] 창에서 이력의
 * 버전을 가져와도 미리보기 렌더가 편집 창에서만 돌아(useSpecRender 의 enabled) `preview.ok` 가
 * 영영 오지 않았고, 저장 잠금은 그 값을 본다 — 오너 보고 「그림을 선택하여 저장할 수 있는
 * 기능이 없음」이 이것이다. 얼굴을 하나로 두면 그 갈래 자체가 없어진다.
 *
 * 이제 `action` 은 **어느 칸을 펼친 채로 열지**만 정한다(지우기만 예외 — 아래).
 *
 *   action='edit'      [편집] — 그냥 연다
 *   action='reconvert' [AI 요청] — 열자마자 재요청 창을 띄운다 (35~100초)
 *   action='replace'   [이미지 교체] — 원본 사진·지난 버전 고르는 칸을 펴고 연다
 *   action='delete'    [지우기] — **합치지 않는다.** 되돌릴 수 없는 쓰기라 편집 폼을 함께
 *                      내지 않는다 — 지우러 온 사람에게 고칠 칸은 소음이고, 잘못 누를 자리다
 *                      (DELETE API — 회신 §2 Q8)
 *
 * 계약: docs/exam-builder-v2/BE-20260827-figure-editing-apis.md (§8 이 2026-08-29 갱신분)
 * 회신: docs/exam-builder-v2/BE-20260829-reply-fe-0828.md §2 — Q1·Q2·Q3·Q5·Q6·Q7·Q8 이 여기서 확정됐다
 *
 * ── 이 창이 지키는 것 넷 ──
 * ① **순번 대조.** 계약의 index 는 저장된 본문의 문서 순서이고, 화면이 센 값과 어긋나면
 *    서버는 **다른 그림에 성공적으로** 저장한다(200 이 온다). 그래서 열자마자 목록을 받아
 *    내가 센 index 의 그림이 정말 내가 누른 그림인지 대조하고, 아니면 쓰기를 막는다.
 * ② **닫힘 차단.** 공용 Modal 은 배경 클릭과 ESC 로 onClose 를 직접 부르고 둘 다 기본값이
 *    켜짐이다. 재요청 100초 도중에 닫히면 새 updated_at 을 못 받아 다음 본문 저장이 영구히
 *    409 로 잠긴다. 그래서 배경 클릭은 항상 끄고, ESC 는 요청 중에만 끈다.
 * ③ **닫을 때 무조건 재조회.** 계약 §6 이 「목록 조회만 해도 이력이 생긴다」고 했다.
 *    그 seed 가 낙관락 토큰을 전진시키지는 **않는다**(회신 Q4 — 실측으로 불변 확인). 그래도 다시
 *    읽는 것은 이 창이 그림을 실제로 고쳤을 수 있고, 본문 쪽 토큰을 최신으로 맞춰 두는 값이 싸서다.
 * ④ **저장 잠금의 기준은 렌더 응답의 ok 하나뿐.** 저장된 명세의 issues 로 잠그면 무효한 명세를
 *    고치러 들어온 사람이 고친 것을 저장할 수 없게 된다.
 * ⑤ **서버가 아는 그림의 삭제는 여기서만 나간다.** 회신 §2 Q8: 편집기에서 image 노드를 지우고 본문
 *    PATCH 로 내보내면 서버가 알 수 없어 이력이 어긋난다 — 지운 그림의 이력이 남고 뒤 그림들의
 *    index 가 밀린다. 그래서 편집기의 [삭제]·Delete·Backspace·Cmd+X 는 노드를 건드리지 않고 이
 *    창을 열기만 하고, 실제 삭제는 DELETE API 다.
 *    **방금 붙여넣어 서버가 아직 모르는 그림은 이 창을 거치지 않는다** — 편집기가 그냥 뺀다.
 *    지울 대상이 서버에 없고, 막으면 「먼저 저장하라」가 되어 원치 않는 그림을 커밋시킨다
 *    (판정은 호스트가 한다 → figureMatch.isSavedFigureSrc · FigureEditContext).
 *    편집기 명령에 걸리지 않는 **범위 선택 삭제**는 호스트의 저장 직전 관문이 막는다
 *    (ProblemViewTab·OcrReviewModal 의 handleSave — 「그림이 줄어든 저장」).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Wand2, ImageOff, Images, History, Save, RefreshCw, Move, Trash2, Plus } from 'lucide-react';
import { Modal, Button } from '../common';
import {
  // restoreFigure 는 **[저장] 시점 1회만** 쓴다(doRestore) — 고르는 행위가 서버로 나가면
  //   둘러보는 것만으로 버전이 쌓이므로(오너 원칙 2026-09-01) 고르기는 여전히 화면 안의 일이고,
  //   spec 없는 판(has_content)의 내용을 되살리는 쓰기가 계약상 restore 뿐이라(「판 내용 스냅샷」
  //   09-01 · figureApi 의 그 함수 주석) 그 한 번만 되살렸다. 즉시 호출로 되돌리지 마라.
  fetchFigures, saveFigure, reconvertFigure, fetchFigureHistory, fetchFigureHistoryVersion,
  restoreFigure, deleteFigure, renderFigspec,
  patchTraceTexts, readTraceTexts, readCorrections, readVersionContent, readEditedCopyDropped,
  registerFigure, replaceFigureImage, parseImageDataUri,
  AUTH_REDIRECT,
} from './figureApi';
import {
  describeFigureError, figureCapabilities, describeFigureMismatch, describeFigureEditedCopy,
  figureOriginalPane, figureOriginPaneLabel, figureOriginPaneNote, FIGURE_NO_COMPARE_TEXT, FIGURE_INEXACT_TEXT,
  figureDropSpecHint, FIGURE_SUGGEST_TRACE_TEXT, FIGURE_MODE_TEXT, figureModeChoices,
  FIGURE_CORRECTIONS_LABEL, FIGURE_AFTER_WRITE_TEXT, FIGURE_TRACE_NO_SPEC_TEXT, figureVersionRow,
  figureEditTabs, FIGURE_EDITED_DROPPED_TEXT,
  describeUnknownFigure, describeDuplicateFigure, describeExtraImagesNote, describeServerExtraNote,
  describeImageReject,
  figurePageCrop, FIGURE_CROP_PANE_LABEL, FIGURE_CROP_UNCHANGED_HINT,
  figDragNote,
} from './figureText';
import { pruneSpec, hasDrawable, DRAWABLE_NAMES } from './figSpecSchema';
// 손으로 옮기기가 열리는가 — 판정을 한 곳에서 재고(figCoords) 그 값을 머리줄 문구와 손잡이 층이 함께 읽는다.
import { figDragState } from './figCoords';
import matchFigure, { matchFigureSequence, findFigureBySrc, countKnownBefore } from './figureMatch';
import useSpecRender from './useSpecRender';
import FigDragLayer from './FigDragLayer';
import FigSpecForm from './FigSpecForm';
import FigureHistoryList from './FigureHistoryList';
import FigureDeleteModal from './FigureDeleteModal';
// 재요청은 이 자리에만 있는 것이 하나 있다 — 원본 이미지 올리기(계약 §4-3 의 1순위 body.image).
import FigureReconvertModal from './FigureReconvertModal';
// 응답마다 실리는 키가 다르다 — 병합 규칙은 검사할 수 있게 따로 두었다(figureItem.js).
import mergeFigureItem from './figureItem';
// 어느 얼굴로 뜨는가 — 규칙을 한자리에 모아 시험으로 덮는다(figureFaces.js 머리주석).
import figureFaces from './figureFaces';
import { FIGURE_SVG_EDITOR } from '../../utils/roles';
import SvgFigureWorkspace from './SvgFigureWorkspace';

/**
 * 창 제목 — **어느 버튼으로 열든 「이미지 편집」 하나다**(2026-09-01 오너 지시 「이미지 편집으로 통일」).
 *
 * 종전에는 누른 버튼 이름을 그대로 제목에 썼다(편집·AI 요청·이미지 교체). 버튼과 창의 이름을
 * 맞추려던 것인데, 창이 2026-08-31 에 **하나로 합쳐진 뒤로는 반대로 작동했다** — 같은 창이 열릴
 * 때마다 이름이 바뀌니 사용자는 매번 다른 곳에 온 줄 알고 무엇을 할 수 있는 자리인지 다시 읽는다.
 * 버튼이 정하는 것은 이제 「열자마자 무엇이 펼쳐지는가」뿐이고(figureFaces), 그건 창 안이 보여 준다.
 *
 * 지우기만 다른 이름을 쓴다 — 합친 셋에 들어 있지 않은, 얼굴이 다른 창이다(2단 · 되돌릴 수 없는 쓰기).
 */
const ACTION_TITLE = {
  edit: '이미지 편집',
  reconvert: '이미지 편집',
  replace: '이미지 편집',
  delete: '이미지 지우기',
};

export default function FigureModal({
  open, target, problemId, field, index, nodeSrc, action = 'edit',
  // 화면이 그리는 그 field 의 그림 줄(문서 순서). 주면 서버 줄과 나란히 맞춰 본다.
  // 단건 대조(matchFigure)는 「내가 누른 그 자리」만 보므로 그림이 통째로 밀린 경우를 놓친다.
  // 안 주면 그 검사를 건너뛴다 — 호출부가 **저장본** 문서를 쥐고 있을 때만 줄 수 있다.
  // 2026-08-29 부터 OCR·문제은행 두 호출부가 모두 넘긴다(회신 Q5·Q6 로 은행도 바이트 대조가 성립).
  nodeList = null,
  // 이 field 에 편집기 편집본(lexical_edited)이 저장돼 있는가. 대조가 어긋났을 때 **원인이 갈린다** —
  // 회신 Q3 이 「편집본에서 바뀐 그림은 편집 불가로 막고 편집본 폐기를 안내하라」고 지정했다.
  // 편집본 개념이 없는 대상(문제은행)은 안 넘기면 된다.
  lexicalEdited = false,
  /**
   * **서버가 모르는 그림을 본문에서 빼 달라**고 호스트에 부탁하는 길(계약 §10 · 회신 §5).
   *
   * 검수자가 편집기에 직접 넣은 이미지는 서버 그림 목록에 없어 삭제 API 로 지울 대상이 없다.
   * 그렇다고 두면 편집본과 원문의 그림 수가 어긋나, 그 field 의 그림을 고칠 때마다 편집본이
   * 폐기된다. 빼는 길이 없으면 남는 것은 [이 문제 다시 OCR] 하나뿐이고 그것은 AI 정답·풀이까지
   * 지운다 — 그 막다른 자리를 이 콜백이 연다.
   *
   * 판정은 **이 창이 한다**(서버 목록을 쥔 쪽이 여기다). 호스트는 시킨 노드를 빼기만 한다.
   * `({ field, src }) => { ok:boolean, removed?:number }` 를 돌려준다. 안 주면 버튼을 내지 않는다.
   */
  onRemoveNode,
  getAccessToken, onClose,
}) {
  /**
   * **얼굴 규칙은 한자리에 있다**(2026-08-31 통일 — figureFaces.js).
   * 편집·AI 요청·이미지 교체는 같은 3단 창을 쓰고, 지우기만 2단으로 남는다(머리주석).
   * 종전에는 `action === 'edit'` 같은 조건이 여섯 자리로 흩어져 있었고, 그중 둘이 어긋나
   * 저장이 영영 잠겼다. 흩어진 조건은 시험으로 덮을 수 없다.
   */
  const faces = figureFaces(action);
  const { deleting } = faces;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [item, setItem] = useState(null);
  /**
   * 그 field 의 서버 그림 **전부**. `item`(누른 하나) 말고 목록이 필요한 자리가 둘이다 —
   * 서버가 모르는 그림을 등록할 때 자리(`at`)를 세는 것과, 「몇 장 대 몇 장」을 말하는 것.
   */
  const [fieldItems, setFieldItems] = useState([]);
  const [match, setMatch] = useState(null);
  const [token, setToken] = useState(null); // 낙관락 — 이 창이 쥐고 매 응답으로 갱신한다
  const [draft, setDraft] = useState(null); // 편집 중인 명세
  const [busy, setBusy] = useState(null); // 'save' | 'reconvert' | 'drop' | version(number)
  const [svgFailure, setSvgFailure] = useState(null);
  const [toast, setToast] = useState(null);  // { type, text, issues[] }
  const [changed, setChanged] = useState(false);

  // 손으로 옮기기. 기본은 켬 — 숫자보다 이쪽이 먼저 손이 간다.
  const [dragOn, setDragOn] = useState(true);
  const [snapStep, setSnapStep] = useState(0.01);

  /**
   * 「무엇으로 바꿀까」 칸을 펼쳤는가 — 원본 사진과 지난 버전이 그 안에 있다.
   * [이미지 교체]로 연 창은 펼친 채로 뜬다(그 버튼을 누른 사람은 이미 고르러 온 것이다).
   */
  const [histOpen, setHistOpen] = useState(figureFaces(action).opensPicker);
  // AI 재요청 창 — [AI 요청]가 바로 나가지 않고 이것을 연다.
  // 35~100초 동기 호출이라 한 번 멈추는 편이 낫고, 그 자리가 지시를 쓰기 가장 좋은 곳이다.
  const [aiOpen, setAiOpen] = useState(false);
  // 삭제 확인 창 — [지우기]가 바로 나가지 않고 이것을 연다. 되돌릴 수 없는 쓰기라 한 번 멈춘다.
  const [delOpen, setDelOpen] = useState(false);
  /**
   * 재요청 창을 열 때 골라져 있을 모드(계약 §9-4). 보통은 기본값이고, 「있는 그대로 옮기기」가
   * 거절하며 `suggest:"trace"` 를 준 뒤 [사진 따라 그리기로 해보기] 를 눌렀을 때만 'trace' 다.
   */
  const [aiMode, setAiMode] = useState('faithful');
  /**
   * 서버가 「사진 따라 그리기라면 될 수 있다」고 알려 왔는가(계약 §9-4 `suggest:"trace"`).
   * 이것이 없으면 거절 응답은 막다른 길로 읽힌다 — 서버가 남은 길을 알려 주는데 화면이 안 낸다.
   */
  const [suggestTrace, setSuggestTrace] = useState(false);
  /**
   * 그림 속 글자 — 「사진 따라 그리기」로 만든 그림에만 있다(계약 §9-4).
   * `{ [id]: content }` 로 들고 있는다. 서버가 준 원본은 item 에 있고, **바뀐 것만** 보낸다.
   */
  const [traceDraft, setTraceDraft] = useState({});
  /**
   * 서버가 바로잡은 곳(계약 §9-4 `corrections[]`). 방금 온 응답의 것만 보인다 —
   * 「이번에 무엇이 다듬어졌나」이지 그림의 성질이 아니라, 다음 요청에 끌고 가지 않는다.
   */
  const [corrections, setCorrections] = useState([]);
  const [hist, setHist] = useState({ loading: false, error: null, versions: null, current: null });
  /**
   * 이력에서 **그려만 보는** 버전(오너 지적 2026-08-30 「다른 버전으로 바꿀 때마다 저장된다」).
   * 이력은 append-only 라(계약 §1-5) [이 버전으로] 를 훑어보듯 누르면 그때마다 새 버전이 쌓인다.
   * history 응답의 versions[] 에 `spec` 이 실려 있고(§3-4) `POST /figspec/render` 는 저장 없이
   * 그려 주므로(§2), **보는 것과 정하는 것을 가를 수 있다.**
   */
  const [previewVer, setPreviewVer] = useState(null);
  /**
   * **불러와서 편집 중인 버전.** 서버의 현재 버전(`hist.current`)과 다른 값이다 —
   * 불러오기는 서버에 보내지 않으므로 `current_version` 은 그대로다.
   * 그 둘을 한 배지로 뭉개면 화면이 「이미 저장됐다」고 거짓을 말한다(2026-08-30 오너 지적:
   * 「선택했으면 이 요소가 그쪽으로 이동해야 되는데 안 됨」). 그래서 표시를 둘로 가른다.
   */
  const [loadedVer, setLoadedVer] = useState(null);
  /**
   * **「사진(보관 원본)으로 바꾸기」를 골랐는가**(2026-09-01 오너 지시 「저장하면 그 버전으로
   * 적용되도록」).
   *
   * 이력의 사진 버전과 좌측 원본 카드가 고르는 것은 **같은 한 장**이다 — 계약 §4-2 는 보관을
   * 「픽셀 → SVG 첫 전환 때」 한 번으로 정했고, `spec:null` 이 돌아가는 곳도 그 한 장이다.
   * 그래서 어느 줄에서 골랐든 저장은 `PATCH {spec:null}` 하나로 나간다.
   *
   * 왜 draft 로 표현하지 않나 — draft 가 null 인 상태는 **이미 두 가지**다: 지금 그림이 픽셀이라
   * 고칠 명세가 없는 것과, 사진으로 바꾸기로 고른 것. 앞엣것에 [저장]을 열면 이미 픽셀인 그림에
   * `spec:null` 을 보내 400 이 온다(§4-2). 둘을 갈라야 저장 잠금이 옳게 걸린다.
   */
  const [pickedOriginal, setPickedOriginal] = useState(false);
  /**
   * **교재 크롭(OCR 당시 지면)으로 바꾸기**를 골랐는가 — 은행 승격 출신 그림에만 있다(계약 §10-2 G5
   * `origin_crop`, 2026-09-05). 사진(보관 원본)과 같은 급의 화면 내 고르기이고 [저장]이 내보내는 것만
   * 다르다 — `PATCH {image: origin_crop}`(이력 950). 오너 요청 2026-08-31 「OCR 당시 원본 PDF 에서
   * 발췌한 이미지를 넣고 싶다」가 이 자리다.
   */
  const [pickedCrop, setPickedCrop] = useState(false);
  /**
   * **「이 판의 그림으로 바꾸기」를 고른 버전**(계약 「판 내용 스냅샷」 09-01) — null 이면 안 골랐다.
   *
   * spec 없는 판인데 내용이 남아 있는 것(has_content=true · 트레이스·픽셀 판)은 pickedOriginal
   * (보관 원본 한 장)과 **다른 곳으로 돌아간다** — 그 판의 실제 그림(글자 수정 포함)이다.
   * `PATCH {spec:null}` 로 보내면 그 내용을 버리므로, [저장]이 restore 를 1회 보낸다(doRestore).
   * pickedOriginal 과 같은 급의 화면 내 고르기다 — 고르기만으로는 서버에 아무것도 나가지 않는다.
   */
  const [pickedContentVer, setPickedContentVer] = useState(null);
  /**
   * 버전마다 **미리 그려 둔 작은 그림**. { [version]: svgDataUri | null }
   *
   * 왜: 오너가 두 번 지적했다 — 「다른 버전으로 옮겨보니 계속 쌓인다」.
   * 이력에서 버전을 고르는 유일한 길이 [이 버전으로](restore)였고, 그것은 append-only 라
   * 누를 때마다 새 버전이 쌓인다(실측: v4~v13 이 전부 사유 500 「되돌리기」였다).
   * [보기]를 더해도 **여전히 눌러야 보인다** — 보는 행위 자체를 없애야 한다.
   * `POST /figspec/render` 는 LLM 도 DB 도 쓰지 않고 수 ms 다(계약 §2). 버전 수만큼 불러 둔다.
   */
  const [thumbs, setThumbs] = useState({});
  /**
   * 판별 **실제 그림** 캐시 — { [version]: {src,width,height,alt} | 'loading' | null(실패) }.
   * GET history/{version}(계약 「판 내용 스냅샷」)의 src 를 담는다. **thumbs 와 섞지 않는다** —
   * 그쪽 값 모양({svg,width,height})은 AI 요청 창이 PNG 로 굽는 데 쓰여 모양이 계약이다.
   *
   * lazy 다 — 목록을 열 때가 아니라 [보기]·고르기가 그 판을 가리킬 때 1건만 받는다(계약
   * 「lazy 권장 — src 가 큼」). 버전은 append-only 로 불변이라(§1-5) 받은 것은 낡지 않는다 —
   * settle 이 thumbs 를 비울 때도 이것은 **안 비운다**.
   */
  const [verSrc, setVerSrc] = useState({});

  const seqRef = useRef(0);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  /**
   * 토큰 얻는 함수를 **ref 로 든다**(2026-08-31 버그 수정).
   *
   * `load` 의 의존성에 이것을 그대로 넣으면 **토큰이 갱신될 때마다 창이 다시 읽는다** —
   * AuthContext 의 getAccessToken 은 `useCallback(…, [accessToken, …])` 이라 접근 토큰이 바뀌면
   * 정체성이 바뀐다(AuthContext.js:147). 하필 이 창의 AI 요청은 **35~100초 동기 호출**이라
   * 그 사이 갱신이 일어나기 딱 좋고, 그때 도는 재조회가 아래 wroteRef 가 막는 사고를 부른다.
   * useSpecRender 가 같은 이유로 이미 같은 수법을 쓴다(그쪽 tokenRef).
   */
  const tokenFnRef = useRef(getAccessToken);
  tokenFnRef.current = getAccessToken;
  /**
   * **이 창이 서버 그림을 이미 바꿨는가.**
   *
   * 바꿨다면 그 순간부터 호스트가 넘겨준 편집기 문서(nodeSrc·nodeList)는 **옛것**이다 —
   * 호스트는 창이 닫힐 때 상세를 다시 읽기 때문이다(ProblemViewTab.closeFigure).
   * 그 상태에서 다시 읽으며 대조하면 **이 창이 스스로 만든 변화 때문에 스스로 잠긴다**:
   * 픽셀 그림을 AI 로 다시 그리면 서버는 `kind:'svg'` 가 되는데 문서의 src 는 아직 png 라
   * matchFigure 가 `reason:'kind'` 로 막고(figureMatch.js:35), writable 이 false 가 되어
   * 저장·AI 요청·그림 교체·지우기가 한꺼번에 잠긴다(오너 보고 2026-08-31 「새로고침이 안 된다」).
   *
   * 순번 대조를 **처음 한 번은 반드시** 한다 — 그것이 이 창의 존재 이유다(§①).
   * 그 뒤로는 내가 무엇을 바꿨는지 내가 알고, 대조할 상대(문서)가 낡은 것이라 검사가 성립하지 않는다.
   */
  const wroteRef = useRef(false);
  useEffect(() => { if (!open) wroteRef.current = false; }, [open]);

  /**
   * **이 창의 쓰기가 편집본을 버렸는가** — 응답의 `edited_copy_dropped`(계약 §10 · 회신 §2).
   *
   * 2026-09-04 부터 서버는 그림을 고칠 때 편집본을 폐기하지 않고 같은 순번의 image 노드만
   * 갈아 끼운다. 다만 순번 대조가 안 되면(검수자가 본문에 참고 이미지를 따로 넣어 둔 편집본)
   * 종전대로 폐기하고 그 사실을 응답에 싣는다.
   *
   * **토스트가 아니라 띠로 남긴다.** 토스트는 몇 초 뒤 사라지는데, 이것은 사라지면 안 되는 손실
   * 통지다 — 사람이 편집기로 돌아갔을 때 「내가 고친 문장이 왜 돌아갔지」의 답이 화면에 있어야 한다.
   * 창을 닫을 때까지 남기고, 창이 다시 열리면 지운다(그때는 지난 일이다).
   */
  const [editedDropped, setEditedDropped] = useState(false);
  useEffect(() => { if (!open) setEditedDropped(false); }, [open]);

  const load = useCallback(async () => {
    const seq = (seqRef.current += 1);
    setLoading(true);
    setLoadError(null);
    try {
      const d = await fetchFigures(target, problemId, tokenFnRef.current);
      if (d === AUTH_REDIRECT || seq !== seqRef.current) return;
      const items = Array.isArray(d?.items) ? d.items : [];
      /**
       * **누른 그림을 먼저 `src` 로 찾는다**(2026-09-04 · 계약 §10 회신 §5).
       *
       * 화면이 넘겨준 `index` 는 **편집본**의 문서 순서인데, 서버의 `index` 는 **원문(payload)**
       * 의 문서 순서다. 검수자가 본문에 참고 이미지를 하나 넣어 두면 그 뒤 그림이 전부 한 칸씩
       * 밀려, 순번으로 찾으면 엉뚱한 그림을 집는다(그리고 서버는 200 을 준다).
       *
       * 바이트가 같은 항목이 **하나뿐일 때만** 성립한다. 같은 그림이 둘이면 원리적으로 못 가르고,
       * 서버가 `src` 를 주지 않는 그림도 대조가 안 된다 — 그때는 종전대로 순번으로 찾아 두고
       * 줄 대조(seq)가 막게 둔다.
       */
      const mineItems = items.filter((it) => it.field === field);
      // 줄 맞춰보기를 함께 싣는다 — 단건 대조가 통과해도 줄이 어긋나 있으면 쓰기를 막는다.
      // 이름은 seqMatch 다 — 이 함수의 seq 는 요청 순번(경쟁 조건 차단)이라 겹치면 안 된다.
      const seqMatch = matchFigureSequence(mineItems, nodeList);
      const byIndex = mineItems.find((it) => Number(it.index) === Number(index)) || null;
      const foundBySrc = findFigureBySrc(items, field, nodeSrc);
      /**
       * 같은 그림이 둘 이상이면 src 로는 못 가른다 — 그러나 **줄 전체가 서버와 같은 순서**면
       * (개수도 같고 자리마다 바이트가 같다) 편집기 순번이 곧 서버 순번이라 자리로 가른다(2026-09-04).
       * 종전에는 이 경우도 「가릴 수 없습니다」로 막아, 같은 그림을 두 번 넣은 문제는 어느 쪽도
       * 고칠 수 없었다. 줄이 어긋난 채 겹치는 경우만 종전대로 막는다(clickedDuplicate).
       */
      const bySrc = (!foundBySrc.ok && foundBySrc.reason === 'duplicate'
        && seqMatch.checked && seqMatch.ok && byIndex && byIndex.src === nodeSrc)
        ? { ok: true, item: byIndex, index: Number(byIndex.index) }
        : foundBySrc;
      const found = bySrc.ok ? bySrc.item : byIndex;
      const m = wroteRef.current
        // 이미 바꾼 뒤다 — 문서가 낡아 대조가 성립하지 않는다(wroteRef 주석). 사실만 남긴다.
        ? { ok: true, reason: null, exact: null, afterWrite: true, seq: { ok: true, reason: null, checked: false } }
        : {
          ...matchFigure(found, nodeSrc),
          seq: seqMatch,
          /**
           * **바이트로 찾았는가** — 찾았으면 줄 대조가 어긋나도 쓰기를 연다(아래 writable).
           *
           * 줄 대조의 목적은 하나다: 「엉뚱한 순번으로 쓰지 않기」. `src` 로 서버 항목을 유일하게
           * 집어냈으면 그 목적은 이미 달성됐고, 남은 개수 차이는 **서버가 모르는 참고 이미지**가
           * 편집본에 섞여 있다는 사실일 뿐이다. 그 하나로 편집·재요청·되돌리기까지 잠그면
           * 사람이 할 수 있는 일이 없어진다(오너 보고 2026-09-04).
           */
          bySrc: bySrc.ok ? { ok: true, index: bySrc.index } : { ok: false, reason: bySrc.reason },
        };
      setItem(found);
      setFieldItems(items.filter((it) => it.field === field));
      setMatch(m);
      setToken(d?.updated_at || null);
      setDraft(found?.spec ? JSON.parse(JSON.stringify(found.spec)) : null);
      setTraceDraft(Object.fromEntries(readTraceTexts(found).map((t) => [t.id, t.content])));
    } catch (e) {
      if (seq !== seqRef.current) return;
      setLoadError(describeFigureError(e).text);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [target, problemId, field, index, nodeSrc, nodeList]);

  useEffect(() => { if (open) load(); }, [open, load]);

  /**
   * `action='reconvert'` 로 열렸으면 **재요청 창을 바로 띄운다**(오너 요청 2026-08-30 「바로 요청하게」).
   * 편집기에서 [AI 요청]을 누른 사람은 이미 재요청을 고른 것이고, 그림 창을 한 번 더
   * 눌러야 하면 그 자리가 두 번이 된다.
   *
   * 로드가 끝난 뒤에 연다 — `needsImage`(보관 원본이 없어 사람이 올려야 하는가)가 item 에서 나오고,
   * 그 전에 열면 올리기 칸 없이 뜬다. 한 번만 연다(ref): 창을 취소한 사람에게 다시 들이밀지 않는다.
   */
  const autoAiRef = useRef(false);
  useEffect(() => { if (!open) autoAiRef.current = false; }, [open]);
  useEffect(() => {
    if (open && faces.opensAi && !loading && item && !loadError && !autoAiRef.current) {
      autoAiRef.current = true;
      setAiMode('faithful');
      setAiOpen(true);
    }
  }, [open, faces.opensAi, loading, item, loadError]);

  // 값이 바뀔 때마다 서버가 그려 준다. 저장 잠금은 **이 응답의 ok** 하나로만 건다(§④).
  const pruned = useMemo(() => (draft ? pruneSpec(draft) : null), [draft]);
  /**
   * 숫자 칸에 **저장하지 않은 고침**이 있는가.
   *
   * 필요한 이유가 하나뿐이다: [글자 저장](그림 속 글자)은 명세를 **보내지 않으면서** 응답의
   * item 으로 draft 를 갈아 끼운다(settle). 그대로 두면 사람이 고치던 좌표·식이 소리 없이 사라진다 —
   * 토스트는 「글자를 바꿨습니다」라고 성공을 말하는데 옆 칸의 값이 돌아가 있는, 조용히 깨지는 종류다.
   * 이 저장소가 [AI 생성]에 이미 같은 관문을 두었다(ProblemViewTab.openFigGen).
   *
   * 문자열로 견준다 — 키 순서가 다를 수 있지만 둘 다 같은 경로(pruneSpec)를 거쳐 나와
   * 사람이 손대지 않으면 같은 글자가 된다. 어긋나 봐야 「저장하라」는 안내가 한 번 더 뜰 뿐이다.
   */
  const specDirty = useMemo(() => {
    const savedRaw = item?.spec ? pruneSpec(item.spec) : null;
    return JSON.stringify(savedRaw ?? null) !== JSON.stringify(pruned ?? null);
  }, [item, pruned]);
  // [보기]로 고른 버전 그 자체 — 「좌표·식인가 사진인가」를 아래 여러 자리가 함께 본다.
  const previewVersion = useMemo(
    () => (previewVer == null ? null
      : (hist.versions || []).find((x) => Number(x.version) === Number(previewVer)) || null),
    [previewVer, hist.versions],
  );
  // 이력에서 고른 버전의 명세. 있으면 **그것을** 그린다 — 편집 중인 draft 는 손대지 않는다.
  const previewSpec = useMemo(
    () => (previewVersion?.spec ? pruneSpec(previewVersion.spec) : null),
    [previewVersion],
  );
  /**
   * 미리보기 칸이 **그 판의 실제 그림**을 그려야 하는 버전(계약 「판 내용 스냅샷」 09-01).
   *
   * [보기]로 보는 중인 spec:null·has_content 판이거나, [이 판의 그림으로 바꾸기]로 골라 둔
   * 판이다. **[보기]·고르기 뒤에만 값이 생긴다** — 목록을 열자마자 전 판의 src 를 받는 길은
   * 계약이 막았다(「lazy 권장 — src 가 큼」). 이 값이 아래 fetch effect 의 유일한 방아쇠다.
   */
  const contentVer = useMemo(() => {
    if (previewVersion) {
      // 판정은 목록과 한 곳(figureVersionRow)을 쓴다 — 여기서 조건을 다시 쓰면 삭제 판(700)
      // 제외 같은 규칙이 한쪽만 고쳐져 [보기]가 404 를 쏘게 된다.
      return figureVersionRow(previewVersion, hist.current).hasContent
        ? Number(previewVersion.version) : null;
    }
    return pickedContentVer;
  }, [previewVersion, pickedContentVer, hist.current]);
  // 그 판의 받아 온 그림 — undefined(아직)·'loading'·{src…}·null(실패). 실패해도 목록은 산다:
  // 칸이 실패를 말하고, [보기]를 다시 누르면 재시도한다(아래 effect 가 ref 에서 지워 둔다).
  const contentEntry = contentVer != null ? verSrc[contentVer] : undefined;
  const previewingContent = contentVer != null;
  /**
   * 미리보기 칸이 **사진**을 그려야 하는가.
   *
   * 이 칸의 약속은 하나다 — 「지금 [저장]을 누르면 무엇이 되는가」. 사진 버전을 보는 중이거나
   * 사진으로 바꾸기를 골라 둔 상태에서는 그 답이 좌표·식이 아니라 **서버가 보관한 원본 한 장**이다
   * (계약 §4-2 — `spec:null` 이 돌아가는 곳은 그 한 장뿐이라 사진 버전들의 답이 모두 같다).
   * has_content 판은 여기 넣지 않는다 — 그 판의 답은 원본이 아니라 **그 판의 내용**이다(위).
   */
  const previewingOriginal = !previewingContent
    && (previewVersion ? previewVersion.spec == null : pickedOriginal);
  // 사진·판 내용을 그릴 때는 렌더를 돌리지 않는다 — 그대로 두면 편집 중이던 값이 대신 그려져,
  // 화면이 그리는 것과 저장될 것이 갈린다.
  const renderSpec = (previewingOriginal || previewingContent) ? null : (previewSpec || pruned);
  const { pending: rendering, result: preview, error: renderError } =
    /**
     * **action 을 가리지 않는다**(2026-08-31 버그 수정).
     *
     * 종전 조건은 `action === 'edit' || previewSpec != null` 이었다. [그림 교체] 창에서 지난 버전을
     * [이 버전 가져오기]로 부르면 `previewVer` 가 null 이 되어(보는 것이 아니라 가져온 것이다)
     * 이 렌더가 **꺼졌고**, 저장 잠금은 `preview?.ok` 를 보므로 저장이 영영 안 열렸다.
     * 화면은 그때 「미리보기가 통과해야 저장할 수 있습니다」라고, 사람이 할 수 있는 일이 없는
     * 말을 했다. 이제 창이 하나라 갈래도 하나다 — 지우기 창에서만 끈다(그릴 폼이 없다).
     */
    useSpecRender(renderSpec, getAccessToken, {
      enabled: !FIGURE_SVG_EDITOR && open && renderSpec != null && faces.renders,
    });

  /**
   * **손으로 옮기기가 열리는가** — 부모가 한 번 재서 두 자리가 같은 값을 읽는다(2026-09-06).
   *   ① 미리보기 칸의 머리줄이 열리지 않는 까닭을 말한다(figDragNote).
   *   ② 손잡이 층(FigDragLayer)은 이 값을 받아 그리기만 한다.
   * 갈라 두면 「머리줄은 안 된다는데 손잡이는 떠 있는」 상태가 생긴다.
   * 켜지 않았거나 그릴 도판이 없으면 재지 않는다 — 없는 것을 두고 까닭을 말할 자리가 아니다.
   */
  const dragState = useMemo(
    () => (dragOn && preview?.ok && preview.svg
      ? figDragState(pruned, { width: preview.width, height: preview.height }, preview.svgText)
      : null),
    [dragOn, pruned, preview],
  );
  const dragNote = figDragNote(dragState);

  /**
   * 이 그림으로 무엇을 할 수 있나 — 지금 쓰는 것은 `canDropSpec`(원본 사진으로 되돌리기) 하나다.
   *
   * `canReconvert` 는 더 보지 않는다(2026-08-31): 은행 재요청이 **늘 화면의 그림을 실어 보내면서**
   * 계약 §4-3 의 1순위가 항상 채워졌다. 그 판정으로 [AI 요청]을 잠그면, 눌리면 되는 버튼을
   * 서버 필드 하나(`has_original`)의 유무로 막는 셈이 된다.
   */
  const caps = figureCapabilities(item, target);

  /**
   * 편집본에서 바이트가 어긋난 그림 — **여기만 막는다**(회신 §2 Q3).
   *
   * 2026-08-30 정정: 한때 바이트 불일치 자체를 matchFigure 가 막았고, 그 판정이 실화면에서
   * 정상 그림까지 잠갔다(오너 보고 「모달은 뜨는데 수정할 수 없다」). 회신이 「편집 불가로 막고
   * 안내」하라고 지목한 것은 **편집본에서 대조가 안 되는 그림** 하나뿐이다 — Q5 는 대조 *방법*을
   * 준 것이지 어긋났을 때의 처분이 아니었다. 그래서 조건을 그 하나로 좁힌다.
   * `target === 'ocr'` 을 함께 거는 이유: 이 안내(describeFigureEditedCopy)가 탈출구로
   * [이 문제 다시 OCR]을 이름으로 부르는데 그 버튼은 OCR 검수 모달에만 있다. 문제은행은
   * 편집본 개념이 없어 lexicalEdited 를 넘기지도 않지만, **문구가 부르는 버튼이 있는 화면**과
   * 조건을 묶어 두어야 다음 사람이 prop 하나로 막다른 화면을 되살리지 못한다.
   */
  /**
   * 2026-09-04 재정정 — 이 판정이 **정상 그림까지 잠그고, 틀린 처방을 냈다**(오너 보고).
   * 조건 둘을 더해 뜨는 자리를 좁힌다.
   *
   * ① `bySrc.ok` — 누른 그림을 **바이트로 정확히 집어냈으면** 막지 않는다. 이 판정의 목적은
   *    「어느 그림인지 확실하지 않을 때 쓰기를 막는 것」인데, 바이트로 찾았으면 확실하다.
   * ② `seq.ok !== false` — 줄(개수)이 어긋났으면 **그쪽 문구가 원인을 더 정확히 짚는다.**
   *    종전에는 이 띠가 먼저 떠서 「[이 문제 다시 OCR]」을 시켰는데, 실제 원인이 개수일 때는
   *    저장 한 번이면 풀리는 것을 AI 정답·풀이까지 지우게 만드는 안내였다.
   */
  const editedMismatch = target === 'ocr' && lexicalEdited === true
    && match?.bySrc?.ok !== true
    && match?.seq?.ok !== false
    && (match?.exact === false || match?.seq?.inexactAt != null);

  /**
   * **서버로 나갈 순번** — 화면이 센 값이 아니라 서버 목록이 준 값이다(계약 §10 · 회신 §5).
   *
   * `index` prop 은 편집기 문서 순서라 편집본에 참고 이미지가 섞이면 밀린다. 목록에서 찾은
   * 항목의 `index` 가 정본이고, 그것이 없을 때만(로딩 중·못 찾음) prop 으로 물러난다.
   *
   * 저장·재요청·되돌리기·삭제·이력·트레이스가 **전부 이 값을 쓴다.** 한 자리라도 prop 을 그대로
   * 쓰면 그 경로만 다른 그림을 가리키고, 서버는 거절하지 않는다.
   */
  const serverIndex = Number.isInteger(Number(item?.index)) ? Number(item.index) : Number(index);

  const writable = match?.ok === true
    // 줄이 어긋나도 **누른 그림을 바이트로 집어냈으면** 연다(match.bySrc 주석).
    && (match?.seq?.ok !== false || match?.bySrc?.ok === true)
    && !editedMismatch && !loadError && item != null;
  const anyBusy = busy != null;

  /**
   * **개수가 어긋난 원인이 「본문에 직접 넣은 이미지」로 확정되는가** — 문서가 더 많고,
   * 그 차이가 전부 서버가 모르는 그림이다(figureMatch 의 unknownInDoc 주석).
   * 이 판정이 서야 아래 세 갈래가 서로 다른 말을 할 수 있다.
   */
  const addedInDoc = match?.seq?.reason === 'count'
    ? match.seq.docCount - match.seq.serverCount : 0;
  const extraImages = addedInDoc > 0 && match?.seq?.unknownInDoc === addedInDoc ? addedInDoc : 0;
  // ① 누른 것이 바로 그 「직접 넣은 이미지」다 — 이 그림에는 어떤 쓰기도 걸 수 없다.
  const clickedUnknown = extraImages > 0 && match?.bySrc?.reason === 'missing';
  // ② 같은 그림이 둘이라 자리를 못 가른다 — 원리적 한계라 아는 척하지 않는다.
  const clickedDuplicate = match?.bySrc?.reason === 'duplicate';
  // ③ 누른 그림은 찾았고 쓰기도 열리지만, 저장하면 편집본이 버려진다는 사실 고지.
  const extraImagesNote = extraImages > 0 && match?.bySrc?.ok === true;
  // ④ 반대 방향 — 서버가 더 많다(본문에서 지운 그림이 서버에 남아 있다). 누른 그림을 바이트로
  //    찾았으면 쓰기는 열려 있고, [저장]이 서버 쪽을 맞춘다(호스트의 purgeRemovedFigures). 고지만 한다.
  const serverExtra = match?.seq?.reason === 'count' && match?.bySrc?.ok === true
    && match.seq.serverCount > match.seq.docCount ? match.seq.serverCount - match.seq.docCount : 0;

  /**
   * **서버가 모르는 그림을 그 자리에서 등록한다** — 2026-09-04 오너 보고의 처방.
   *
   * 종전에는 이 자리에 [본문에서 빼기] 하나만 냈다. 그래서 붙여넣은 이미지를 누르면
   * 「빼는 것 말고는 아무것도 못 하는」 화면이 됐다(오너 보고 「AI 요청도 안 되고 재생성도
   * 안 되고 그림을 고칠 수도 없고 삭제만 가능 — 이건 내가 원하는 것이 아님」).
   * 등록하면 그 이미지가 서버 그림이 되어 편집·AI 요청·교체·되돌리기가 한꺼번에 열린다.
   *
   * **이미 본문에 있는 이미지를 등록하는 경로**를 서버가 이미 대비해 두었다 — 회신 §2 insert:
   * 「노드 수 == 그림 수이고 `index` 노드 `src` 가 이미 같은 바이트면(FE 가 편집본에 먼저 넣고
   * 등록) 무변경」. 즉 편집본에 노드가 겹쳐 두 장이 되지 않는다.
   *
   * 자리(`at`)는 **이 노드 앞의 그림 중 서버가 아는 것만** 세어 정한다. 참고 이미지가 여럿이면
   * 편집기 순번과 서버 순번이 그만큼 어긋나기 때문이다. 셀 수 없으면(nodeList 를 못 받았거나
   * 대조가 안 서면) 싣지 않고 서버가 맨 끝에 넣는다.
   */
  const registerAt = Array.isArray(nodeList)
    ? countKnownBefore(nodeList.slice(0, Number(index)).map((n) => n?.src), fieldItems, field)
    : null;
  const pastedCheck = parseImageDataUri(nodeSrc);
  const canRegisterNode = target === 'ocr';

  const doRegisterNode = async () => {
    if (!pastedCheck.ok) {
      setToast({ type: 'error', text: describeImageReject(pastedCheck), issues: [] });
      return;
    }
    setBusy('register');
    setToast(null);
    try {
      const d = await registerFigure(target, problemId,
        { field, at: registerAt ?? undefined, image: nodeSrc, expectedUpdatedAt: token }, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      // 서버가 payload 에 그림을 넣었고 편집본은 그대로다(같은 바이트가 같은 자리 — 회신 §2).
      // 창을 닫고 호스트가 상세를 다시 읽으면 그때부터 이 그림은 서버가 아는 그림이다.
      closeRef.current?.(true, { registered: true, field, index: d?.index });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const canRemoveNode = typeof onRemoveNode === 'function' && target === 'ocr';
  const doRemoveNode = () => {
    const r = onRemoveNode?.({ field, src: nodeSrc });
    // 못 뺐으면 창을 닫지 않는다 — 닫으면 아무 일도 안 일어난 것처럼 보인다.
    if (r?.ok) {
      // 서버는 그대로다(changed:false) — 호스트가 상세를 다시 읽으면 방금 뺀 노드가 되돌아온다.
      closeRef.current?.(false, { removedNode: true, field });
    } else {
      setToast({ type: 'error', text: '본문에서 그 이미지를 찾지 못했습니다 — [편집기] 탭에서 직접 지워 주세요.', issues: [] });
    }
  };

  /**
   * **이 삭제가 편집본을 버리게 되는가** — 삭제 확인창이 겁을 줄지 말지를 가른다.
   *
   * 계약 §10(회신 §2)의 삭제 동기화 조건은 「편집본 image 노드 수 == 서버 그림 수 + 1」이다.
   * 지금 편집기 문서와 서버가 같은 수라면(docCount === serverCount) 지운 뒤 그 조건이 성립해
   * 서버가 노드만 빼고 문장은 남긴다. 수가 다르면(참고 이미지가 섞인 편집본) 폐기된다.
   *
   * **모르면 넘기지 않는다** — 대조를 건너뛴 경우(checked:false, 이 창이 이미 쓴 뒤)에는
   * undefined 가 되어 확인창이 그 줄을 아예 내지 않는다. 모르는 것으로 겁을 주지 않는다.
   */
  const deleteDropsEdited = lexicalEdited === true && match?.seq?.checked === true
    ? match.seq.docCount !== match.seq.serverCount
    : undefined;

  // 대조 사유인가 — fail() 이 내리는 서버 거절(409·404·423)과 가르는 데 쓴다.
  const isCompareReason = (reason) => ['count', 'kind', 'src', 'missing'].includes(reason);

  const settle = (d, okText) => {
    setSvgFailure(null);
    if (d?.updated_at) setToken(d.updated_at);
    if (d?.item) {
      setItem((prev) => mergeFigureItem(prev, d.item));
      setDraft(d.item.spec ? JSON.parse(JSON.stringify(d.item.spec)) : null);
      // 트레이스 결과가 새 글자 목록을 실어 왔으면 그것으로 갈아 끼운다. 안 실려 오면
      // mergeFigureItem 이 앞 목록을 지켜 주므로(figureItem.js) 화면의 칸이 사라지지 않는다.
      const next = readTraceTexts(d);
      if (next.length > 0) setTraceDraft(Object.fromEntries(next.map((t) => [t.id, t.content])));
    }
    setChanged(true);
    wroteRef.current = true; // 이제 편집기 문서는 옛것이다 — 다시 읽어도 대조하지 않는다(wroteRef 주석)
    setHist({ loading: false, error: null, versions: null, current: null }); // 이력이 늘었다 — 다시 읽게 비운다
    setPreviewVer(null); // 그림이 바뀌었다 — 옛 버전을 보고 있던 상태를 끌고 가지 않는다
    setLoadedVer(null);  // 저장됐으니 「불러옴(저장 전)」이 아니다 — 서버 현재 버전이 정본이다
    setPickedOriginal(false); // 고른 것이 반영됐다 — 「사진으로 바꾸기」 표시를 끌고 가지 않는다
    setPickedCrop(false);
    setPickedContentVer(null); // 판 그림 고르기도 같다 — 반영됐거나 다른 저장이 앞질렀다
    setThumbs({});                 // 버전이 하나 늘었다 — 다시 그린다
    thumbReqRef.current = new Set(); // 요청 기록도 함께 비운다(안 그러면 새 버전을 영영 안 그린다)
    // verSrc 는 **안 비운다** — 버전은 append-only 로 불변이라(계약 §1-5) 받아 둔 판 그림은 낡지 않는다.
    setSuggestTrace(false);          // 그려졌다 — 「따라 그리기로 해보라」는 제안은 이제 뜻이 없다
    setCorrections(readCorrections(d)); // 이번 응답이 바로잡았다고 말한 것만(안 오면 빈 목록)
    // 편집본이 버려졌으면 **끄지 않는다** — 이 창에서 한 번이라도 그 일이 났으면 창이 닫힐 때까지
    // 띠를 남긴다. 다음 쓰기가 보존됐다고 해서 앞서 잃은 문장이 돌아오는 것은 아니다.
    if (readEditedCopyDropped(d)) setEditedDropped(true);
    setToast({ type: 'info', text: okText, issues: [] });
  };

  const fail = (e) => {
    if (FIGURE_SVG_EDITOR && !deleting && ['stale', 'stale_revision'].includes(e?.data?.reason)) {
      setSvgFailure(e); return;
    }
    const d = describeFigureError(e);
    setToast({ type: 'error', text: d.text, issues: d.issues });
    // 되살릴 수 없는 것(남이 먼저 바꿈·없는 그림·잠김)은 그대로 두면 계속 실패한다.
    // `fromError` 로 **출처를 남긴다** — 여기 실리는 reason 은 'stale'·'locked'·'not_found'·
    // 'bad_request' 처럼 **서버가 거절한 사유**이지 대조 실패가 아니다. 표시가 없으면 화면이
    // 「내가 누른 그림과 서버가 준 그림이 서로 다릅니다 … 본문을 저장한 뒤 다시 열어 주세요」라는
    // 원인도 처방도 틀린 띠를 내고, 바로 위 토스트(「창을 닫고 다시 여세요」)와 반대되는 일을 시킨다.
    if (!d.recoverable) setMatch({ ok: false, reason: d.reason, fromError: true });
  };

  const doSave = async (spec, source) => {
    if (!writable || anyBusy) return;
    setBusy(spec == null ? 'drop' : 'save');
    setToast(null);
    try {
      const d = await saveFigure(target, problemId, field, serverIndex, { spec, source, expectedUpdatedAt: token }, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      settle(d, spec == null ? '원본 그림으로 되돌렸습니다.' : '저장되었습니다.');
    } catch (e) { fail(e); } finally { setBusy(null); }
  };

  /**
   * **판 내용 복원 — restore 를 [저장] 시점에 1회만 부른다**(계약 「판 내용 스냅샷」 09-01).
   *
   * spec 없는 판(트레이스·픽셀, has_content=true)의 내용을 되살리는 쓰기는 restore 뿐이다 —
   * 그 내용을 싣는 PATCH 가 계약에 없고, `PATCH {spec:null}` 은 판 내용(글자 수정 포함)을 버리고
   * 보관 원본으로 가 버린다. 오너 원칙(「고르기는 서버에 안 나간다」)과 양립한다 — 원칙의 요지는
   * 둘러보기가 쓰기가 되면 안 된다는 것이지 restore 금지가 아니다. 고르기(pickContentVersion)는
   * 화면 안의 일이고, 이 호출은 [저장]을 눌렀을 때 한 번 나간다 — 버전은 여전히 그때 하나만 는다.
   * 이력 사유가 400(편집) 대신 500(되돌리기)로 남고 restored_from 이 기록되는 것은 감수한다 —
   * 2026-08-30 에 걷어낸 것(ddeb092)은 「누를 때마다 즉시 restore」이지 이 한 번이 아니다.
   *
   * 응답 { item, restored_from, updated_at } 은 settle 이 그대로 소화한다.
   */
  const doRestore = async () => {
    if (!writable || anyBusy || pickedContentVer == null) return;
    setBusy('restore');
    setToast(null);
    try {
      const d = await restoreFigure(
        target, problemId, field, serverIndex, pickedContentVer,
        { expectedUpdatedAt: token }, getAccessToken,
      );
      if (d === AUTH_REDIRECT) return;
      settle(d, `v${pickedContentVer} 의 그림 그대로 되돌렸습니다.`);
    } catch (e) { fail(e); } finally { setBusy(null); }
  };

  /**
   * AI 재요청. `useOriginal` 은 계약 §9-4(2026-09-01)의 「이력 보관 원본으로 강제 재생성」이다 —
   * 그때는 이미지를 싣지 않는다(서버가 무시한다 · buildReconvertPayload).
   */
  const doReconvert = async ({ userPrompt, image, mode, useOriginal, sourceLabel } = {}) => {
    if (!writable || anyBusy) return;
    setBusy('reconvert');
    setToast(null);
    setSuggestTrace(false);
    setCorrections([]);
    try {
      const d = await reconvertFigure(
        target, problemId, field, serverIndex,
        { expectedUpdatedAt: token, userPrompt, image, mode, useOriginal },
        getAccessToken,
      );
      if (d === AUTH_REDIRECT) return;
      if (d?.converted === false) {
        // 실패가 아니다 — 좌표·식으로 옮길 수 없는 그림이라 원본을 그대로 둔 것이고 손실이 없다.
        /**
         * 다만 **막다른 길로 두지는 않는다**(계약 §9-4). 서버가 `suggest:"trace"` 를 실어 보내면
         * 「사진이라 좌표·식으로는 못 옮기지만 윤곽을 따라 그릴 수는 있다」는 뜻이다.
         * 그 길이 이 화면에 실제로 있을 때만 낸다 — 계약이 트레이스를 **은행 재요청만**으로
         * 좁혔으므로 OCR 검수에서는 제안을 받아도 낼 버튼이 없다(figureModeChoices 와 같은 근거).
         */
        const canTrace = d?.suggest === 'trace'
          && figureModeChoices(target === 'ocr' ? 'ocr-reconvert' : 'bank-reconvert').includes('trace');
        setSuggestTrace(canTrace);
        setToast({
          type: 'info',
          text: FIGURE_SVG_EDITOR ? 'AI가 그림을 다시 그리지 못했습니다. 기존 그림은 그대로입니다.' : canTrace
            ? `이 그림은 좌표·식으로 옮길 수 없어 원본을 그대로 두었습니다(손실 없음). ${FIGURE_SUGGEST_TRACE_TEXT}`
            : '이 그림은 좌표·식으로 옮길 수 없어 원본을 그대로 두었습니다(손실 없음).',
          issues: Array.isArray(d.issues) ? d.issues : [],
        });
        return;
      }
      /* 무엇을 보고 그렸는지 **서버는 응답에 적지 않는다** — 화면이 보낸 것을 알고 있으므로
         여기서 말한다. 재료가 다르면 결과도 다른데, 구분해 말하지 않으면 두세 번 눌러 본 사람이
         어느 쪽 결과를 보고 있는지 모른다.
         2026-09-01: 고를 수 있는 것이 넷이 되어(지금 그림·원본·이력 v…·올린 것) `useOriginal`
         하나로는 갈리지 않는다 — 재요청 창이 고른 이름을 그대로 실어 보낸다(sourceLabel).
         2026-09-06: 원본이 **왼쪽 칸으로 돌아와** 늘 보이므로(오너 지시 「표출부터」), 「펴세요」를 뺀다 —
         이미 펴져 있는 것을 펴라고 하면 사람이 없는 버튼을 찾는다. 원본이 없는 그림에서는 그 칸이 없다고
         말하고 있으므로 여기서 되풀이하지 않는다. */
      settle(d, `AI 가 「${sourceLabel || (useOriginal ? '보관된 원본' : '지금 그림')}」을 보고 그림을 다시 만들었습니다.`
        + ' 왼쪽 원본 칸과 견주어 보세요.');
    } catch (e) { fail(e); } finally { setBusy(null); }
  };

  /**
   * 그림 속 글자만 바꾼다 (계약 §9-4).
   *
   * **바뀐 것만 보낸다.** 안 바뀐 글자까지 실으면 서버가 그것도 사람이 고친 값으로 버전에 남긴다.
   * 하나도 안 바뀌었으면 아예 나가지 않는다 — 누를 때마다 버전이 쌓이는 자리를 또 만들지 않는다
   * (이력 restore 에서 이미 한 번 겪었다 · figureApi.restoreFigure 주석).
   *
   * ⚠ 응답 `{applied, item}` 에 **`updated_at` 이 온다는 말이 계약에 없다.** 안 오면 이 창이 쥔
   * 낙관락이 낡은 채로 남아 다음 저장이 409 `stale` 로 잠긴다. 그래서 안 왔을 때는 목록을 다시
   * 읽어 토큰을 채운다 — 「모르면 멈춘다」가 아니라 「모르면 확인한다」로 갈 수 있는 자리다.
   */
  const doSaveTraceTexts = async () => {
    if (!writable || anyBusy) return;
    // 숫자 칸의 미저장분을 덮지 않는다 — 아래 settle 이 draft 를 서버 값으로 갈아 끼운다(specDirty 주석).
    if (specDirty) {
      setToast({
        type: 'error',
        text: '숫자·식에 저장하지 않은 고침이 있습니다 — 먼저 [저장]을 눌러 주세요. 글자만 보내면 그 고침이 사라집니다.',
        issues: [],
      });
      return;
    }
    const before = Object.fromEntries(readTraceTexts(item).map((t) => [t.id, t.content]));
    const changed = Object.fromEntries(
      Object.entries(traceDraft).filter(([id, v]) => (before[id] ?? '') !== v),
    );
    if (Object.keys(changed).length === 0) {
      setToast({ type: 'info', text: '바뀐 글자가 없습니다.', issues: [] });
      return;
    }
    setBusy('trace');
    setToast(null);
    try {
      const d = await patchTraceTexts(
        target, problemId, field, serverIndex,
        { traceTexts: changed, expectedUpdatedAt: token },
        getAccessToken,
      );
      if (d === AUTH_REDIRECT) return;
      /**
       * **보낸 것과 적용된 것을 대조한다.** 응답의 `applied` 는 서버가 실제로 갈아 끼운 id 목록이다
       * (실측: `{applied:["t2"], …}`). 여럿을 한꺼번에 고쳤을 때 일부만 들어가면, 대조하지 않는
       * 화면은 「바꿨습니다」라고 말하면서 안 바뀐 글자를 그대로 둔다 — 조용히 틀리는 종류다.
       * 서버가 이 키를 안 보내면 대조하지 않는다(모르는 것으로 실패를 지어내지 않는다).
       */
      const sent = Object.keys(changed);
      const applied = Array.isArray(d?.applied) ? d.applied.map(String) : null;
      const missed = applied ? sent.filter((id) => !applied.includes(id)) : [];
      settle(d, missed.length === 0
        ? '그림 속 글자를 바꿨습니다. 이력에 버전으로 남습니다.'
        : `${sent.length - missed.length}개를 바꿨습니다. ${missed.join('·')} 는 서버가 적용하지 않았습니다 — 다시 확인해 주세요.`);
      if (!d?.updated_at) await load(); // 토큰을 못 받았다 — 다음 저장이 잠기기 전에 다시 읽는다
      if (missed.length > 0) {
        /**
         * 안 들어간 칸은 **사람이 친 글자를 지킨다.** settle 이 traceDraft 를 서버 값으로 통째로
         * 갈아 끼우므로(그 함수의 readTraceTexts), 그대로 두면 「적용하지 않았습니다」라고 말하는
         * 그 순간 옆 칸의 값이 옛 글자로 돌아가 있다 — 다시 치라는 말도 못 하게 된다.
         * 그리고 토스트를 **붉게** 다시 세운다. settle 은 늘 'info' 라, 부분 실패가 성공과 같은
         * 색으로 뜨면 아무도 안 읽는다.
         *
         * **위 load() 보다 뒤에 둔다** — 그쪽도 traceDraft 를 다시 깔기 때문에, 앞에 두면
         * 지켜 둔 값이 그 자리에서 도로 덮인다.
         */
        setTraceDraft((prev) => ({ ...prev, ...Object.fromEntries(missed.map((id) => [id, changed[id]])) }));
        setToast({
          type: 'error',
          text: `${missed.join('·')} 는 서버가 적용하지 않았습니다 — 그 칸의 값은 그대로 두었으니 다시 저장해 보세요.`,
          issues: [],
        });
      }
    } catch (e) { fail(e); } finally { setBusy(null); }
  };

  const loadHistory = useCallback(async () => {
    setHist({ loading: true, error: null, versions: null, current: null });
    try {
      const d = await fetchFigureHistory(target, problemId, field, serverIndex, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      setHist({
        loading: false, error: null,
        versions: Array.isArray(d?.versions) ? d.versions : [],
        current: d?.current_version ?? null,
      });
      // history 응답의 item 에는 original_src 가 실린다(회신 §2 Q7) — 그래도 같은 병합을 쓴다.
      // 규칙이 한 벌이어야 「어느 응답은 덮고 어느 응답은 병합한다」를 다음 사람이 외우지 않는다.
      if (d?.item) setItem((prev) => mergeFigureItem(prev, d.item));
    } catch (e) {
      setHist({ loading: false, error: describeFigureError(e).text, versions: null, current: null });
    }
  }, [target, problemId, field, serverIndex, getAccessToken]);

  /**
   * 이력이 보이면 아직 안 그린 버전을 병렬로 그린다. 한 버전당 한 번만.
   *
   * ⚠ **`thumbs` 를 의존성에 넣지 마라.** 자리를 잡으려 `setThumbs` 를 부르는 순간 상태가 바뀌어
   * 이 effect 의 cleanup 이 돌고, 그러면 `alive` 가 false 가 되어 **막 도착한 결과를 버린다**
   * (2026-08-30 에 실제로 그렇게 만들어 썸네일이 하나도 안 떴다 — 로딩 자리에서 멈췄다).
   * 「이미 요청한 버전」은 상태가 아니라 ref 로 기억한다 — 그것은 렌더에 쓰이지 않는 사실이다.
   */
  const thumbReqRef = useRef(new Set());
  /**
   * 불러온 결과(가운데 미리보기·폼)가 있는 자리 — 이력에서 부르면 화면 밖이라 데려가야 한다.
   *
   * 실측 2026-08-30: 불러오기 버튼은 이력(창 아래, y≈2270)에 있고 결과는 창 위(y≈2215 → 화면 밖)다.
   * 그대로 두면 눌러도 화면상 아무 일도 안 일어난 것처럼 보이고, 오너가 「불러오기 자체가 안 되는
   * 것 같다」고 한 것이 이것이다([보기]에서 같은 실수를 이미 한 번 했다).
   *
   * ⚠ `requestAnimationFrame` 으로는 안 된다 — 그 시점에 아직 DOM 이 갱신 전이라 스크롤이
   * 옛 좌표로 나간다(실제로 그렇게 만들어 안 움직였다). **effect 로 미룬다.**
   */
  const editAreaRef = useRef(null);
  const [scrollToEdit, setScrollToEdit] = useState(0);
  useEffect(() => {
    if (!scrollToEdit) return;
    editAreaRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [scrollToEdit]);
  /**
   * [보기]를 누르면 **그 결과가 그려지는 위 두 칸으로 데려간다.**
   *
   * 종전에는 이력 안의 미리보기 상자로 데려갔는데, 2026-09-01 오너 지시로 그 상자를 걷었다
   * (「어차피 위에 보이므로」). 데려가기까지 함께 걷으면 2026-08-30 의 그 문제 —
   * 눌러도 결과가 화면 밖이라 아무 일도 안 난 것처럼 보이는 것 — 가 그대로 돌아온다.
   * 상자를 없앨 수 있는 것은 이 스크롤이 남아 있기 때문이다.
   */
  useEffect(() => {
    if (previewVer == null) return;
    editAreaRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [previewVer]);
  /**
   * ⚠ AI 요청 창이 열려 있을 때도 그린다(2026-09-01) — 그 창이 **버전별로 고르게** 하려면
   * 버전 그림이 있어야 하고, 그림의 출처는 여기 하나다. 렌더를 창마다 새로 짜면 같은 버전을
   * 두 번 그리게 된다.
   */
  useEffect(() => {
    if (FIGURE_SVG_EDITOR || !open || (!histOpen && !aiOpen)) return;
    const todo = (hist.versions || []).filter(
      (v) => v.spec != null && !thumbReqRef.current.has(v.version),
    );
    if (!todo.length) return;
    todo.forEach((v) => thumbReqRef.current.add(v.version));
    /* **크기도 함께 담는다**(2026-09-01). 그림만 담아 두었더니 AI 요청 창이 그 SVG 를 PNG 로
       구울 때 크기를 넘길 수 없었고, figureImage 는 `naturalWidth || width` 가 0 이면 거절한다 —
       버전 카드가 첫 클릭에 「보낼 수 없음」으로 굳는 길이 여기서 열렸다. 서버가 준 값을 버리지 않는다. */
    Promise.all(todo.map((v) => renderFigspec(pruneSpec(v.spec), getAccessToken)
      .then((r) => ({ v: v.version, hit: r?.ok ? { svg: r.svg, width: r.width, height: r.height } : null }))
      .catch(() => ({ v: v.version, hit: null }))))
      .then((rs) => {
        setThumbs((prev) => {
          const next = { ...prev };
          rs.forEach(({ v, hit }) => { next[v] = hit; });
          return next;
        });
      });
  }, [open, histOpen, aiOpen, hist.versions, getAccessToken]);

  /**
   * [보기]·고르기가 가리킨 has_content 판의 실제 그림을 **그때 1건만** 받는다(계약 「판 내용
   * 스냅샷」 — 「lazy 권장 — src 가 큼」). 방아쇠가 contentVer 하나라, 창 열림·목록 로드 직후에는
   * 절대 나가지 않는다 — 첫 조회가 관측(version 1)을 기록하는 규칙(계약 §6)의 영향권을 넓히지 않는다.
   *
   * ⚠ 위 thumbs effect 의 함정이 그대로 적용된다 — **verSrc 를 의존성에 넣지 마라**(cleanup 이
   * 막 도착한 결과를 버린다). 「이미 요청한 판」은 ref 로 기억한다. 실패한 판만 ref 에서 지운다 —
   * [보기]를 껐다 다시 누르면 재시도할 길을 남긴다(성공분은 불변이라 영영 다시 안 받는다).
   * 순번 가드는 없다 — 결과가 버전 키로 담기고 버전은 불변이라(§1-5) 늦게 온 응답도 제 자리가 맞다.
   */
  /**
   * AI 창에서 **재료로 고른** 판(2026-09-06). 미리보기용(contentVer)과 갈라 둔다 — 재료로 골랐다고
   * 뒤 칸의 미리보기까지 그 판으로 바뀌면 「지금 저장하면 무엇이 되는가」라는 그 칸의 약속이 거짓이 된다.
   */
  const [aiWantVer, setAiWantVer] = useState(null);
  /** 지금 받아야 할 판들 — 둘 다 같은 lazy 규칙(고른 것만 1건)을 따른다. */
  const needVers = useMemo(() => {
    const out = [];
    if (contentVer != null) out.push(contentVer);
    if (aiWantVer != null && aiWantVer !== contentVer) out.push(aiWantVer);
    return out;
  }, [contentVer, aiWantVer]);
  const verReqRef = useRef(new Set());
  // 실패 후 재시도 논스 — [보기] 토글로는 contentVer 가 안 변해(골라 둔 판이 같은 번호를 돌려준다)
  // effect 가 다시 돌 길이 없다. 실패 칸의 [다시 시도]가 이 값을 올려 명시적으로 태운다.
  const [verRetry, setVerRetry] = useState(0);
  useEffect(() => {
    if (!open || needVers.length === 0) return;
    needVers.forEach((ver) => {
      if (verReqRef.current.has(ver)) return;
      verReqRef.current.add(ver);
      setVerSrc((prev) => ({ ...prev, [ver]: 'loading' }));
      fetchFigureHistoryVersion(target, problemId, field, serverIndex, ver, tokenFnRef.current)
        .then((d) => {
          if (d === AUTH_REDIRECT) return;
          const hit = readVersionContent(d);
          // src 없이 온 응답도 실패와 같은 폴백이다 — 재시도 길을 남긴다.
          if (!hit) verReqRef.current.delete(ver);
          setVerSrc((prev) => ({ ...prev, [ver]: hit }));
        })
        .catch(() => {
          verReqRef.current.delete(ver);
          setVerSrc((prev) => ({ ...prev, [ver]: null }));
        });
    });
  }, [open, needVers, verRetry, target, problemId, field, serverIndex]);

  /**
   * 이력을 언제 읽는가.
   *
   * 창을 열 때 자동으로 읽지 않는 규칙은 그대로다 — **첫 조회가 version 1(관측)을 기록**하기
   * 때문이다(계약 §6). 다만 2026-09-01 오너 지시로 AI 요청 창에서 지난 버전을 고르게 됐고,
   * 고를 목록이 있으려면 그 창이 뜰 때는 읽어야 한다. 그 창을 여는 사람은 이미 그림을 다시
   * 만들기로 한 사람이라, 관측 한 줄이 이력에 남는 대가는 「고를 수 없다」보다 싸다.
   */
  useEffect(() => {
    if (!FIGURE_SVG_EDITOR && open && (histOpen || aiOpen) && hist.versions == null && !hist.loading && !hist.error) loadHistory();
  }, [open, histOpen, aiOpen, hist.versions, hist.loading, hist.error, loadHistory]);

  /**
   * AI 요청 창에 낼 **지난 버전 후보**(2026-09-01 오너 지시 「버전별로 선택할 수 있도록」).
   *
   * ── 2026-09-06: **내용이 남은 판도 재료가 된다**(오너 지시 「각 그림들의 버전 모두 … AI 에 요청」) ──
   * 종전에는 좌표·식(spec)이 있는 판만 카드로 냈다. 근거는 「그 src 는 커서 [보기] 전에는 없다」였는데,
   * 그건 **미리 받지 않는다**는 뜻이지 **못 쓴다**는 뜻이 아니다. 등록(900)·교체(950)·트레이스(300) 판은
   * 좌표·식이 없어도 제 그림이 서버에 남아 있고(계약 「판 내용 스냅샷」), 그 그림이야말로 다시 그릴 재료다.
   * 이제 카드는 내고 **고른 순간에 받는다**(needs → 창이 onNeedVersion 을 부르고 아래 fetch effect 가 1건 받는다).
   *
   * 걸러내는 것이 넷이고, 이유가 각각 다르다 —
   *   · 사진 판(spec 도 has_content 도 없음): 돌아갈 곳이 보관 원본 한 장이라 [원본 사진] 카드와 같은 것이 된다(§4-2).
   *   · 서버의 현재 버전: 그것이 곧 [지금 그림] 카드다. 같은 그림에 이름만 둘 붙이지 않는다.
   *   · 렌더가 실패한 버전(`thumbs[v] === null`): 보낼 그림을 만들 수 없다 — 눌리면 반드시
   *     실패하는 카드를 내지 않는다. 아직 안 그려진 것(키 없음)은 자리를 잡아 둔다.
   *   · 받아 봤는데 그림이 없던 판(`verSrc[v] === null` — 서버가 그 길을 안 열었거나 실패): 같은 규칙으로 뺀다.
   * 판정(spec 인가 내용 판인가)은 목록과 **같은 함수**를 쓴다 — 삭제 판(700) 제외 같은 규칙이 갈리면
   * 카드는 뜨는데 [보기]는 404 를 쏘는 상태가 된다.
   */
  const aiVersions = useMemo(() => {
    if (!Array.isArray(hist.versions)) return [];
    return hist.versions
      .filter((v) => Number(v.version) !== Number(hist.current))
      .map((v) => {
        const n = Number(v.version);
        const row = figureVersionRow(v, hist.current);
        if (row.hasSpec) {
          return {
            version: n,
            src: thumbs[n]?.svg || null,
            // 굽는 데 쓴다 — SVG 안에 치수가 없으면 이 값이 없이는 PNG 로 못 굽는다(figureImage).
            width: thumbs[n]?.width ?? null,
            height: thumbs[n]?.height ?? null,
            alt: v.alt || '',
            drawing: !(n in thumbs),
          };
        }
        if (!row.hasContent) return null;
        const got = verSrc[n];
        if (got === null) return null;
        const loading = got === 'loading';
        const hit = got && !loading ? got : null;
        return {
          version: n,
          src: hit?.src || null,
          width: hit?.width ?? null,
          height: hit?.height ?? null,
          alt: v.alt || '',
          // 아직 손에 없다 — 카드는 내되 고른 순간에 받는다(needs). 받는 중이면 그 사실을 카드가 말한다.
          drawing: !hit,
          loading,
          needs: !got,
        };
      })
      .filter(Boolean)
      .filter((v) => v.src || v.drawing);
    // 의존성이 hist 통짜인 이유: exhaustive-deps 가 `hist.current`(서버의 현재 버전 번호)를
    // ref 의 `.current` 로 잘못 읽고 경고한다. hist 는 setHist 로 통째로 갈리는 상태라 넓혀도 옳다.
  }, [hist, thumbs, verSrc]);

  /**
   * 내용이 남은 판(has_content)이 목록에 하나라도 있는가 — 머리글의 「사진 버전은 모두 …
   * 그림이 같습니다」가 그때는 거짓이 되므로(그 판들은 제각각 제 그림으로 돌아간다) 그 문장을
   * 조건부로 낸다. 키가 안 오는 구 배포에서는 거짓 → 종전 문구 그대로다.
   */
  const anyContentVer = useMemo(
    () => (hist.versions || []).some((v) => v.spec == null && v.has_content === true),
    [hist.versions],
  );

  /**
   * 지난 버전을 **불러오기만 한다** — 서버에 아무것도 보내지 않는다.
   *
   * 2026-08-30, 오너 지적 세 번 만에 바로잡은 것:
   * 「버튼을 누를 때 확인만 하게 불러와 주고, 저장 버튼을 눌렀을 때만 이력 저장을 하면 되는 것인데」.
   *
   * 종전에는 `POST …/history/{version}/restore` 를 바로 불렀다. 계약이 그 API 를 「되돌리기」로
   * 주었기에 되돌리기는 곧 그 호출이라고 굳게 잡았고, 이력이 append-only 라(§1-5) 누를 때마다
   * 버전이 쌓이는 것을 **어쩔 수 없다**고 결론냈다. 그것이 틀렸다 —
   * §3-2 의 `PATCH {spec, source:'edit'}` 로 같은 그림에 이를 수 있고, 그러면
   * **불러오기는 저장이 아니고 저장은 [저장]을 눌렀을 때만**이 된다. 버전은 사람이 정할 때 하나만 는다.
   *
   * 그래서 restore 는 쓰지 않는다. 잃는 것은 이력에 남는 사유가 500(되돌리기) 대신 400(편집)이
   * 되는 것과 `restored_from` 이 안 남는 것뿐인데, 몇 개를 둘러봤는지가 이력에 쌓이는 대가보다 싸다.
   */
  const loadVersion = (version) => {
    if (!writable || anyBusy) return;
    const v = (hist.versions || []).find((x) => Number(x.version) === Number(version));
    if (!v || v.spec == null) return;
    setDraft(JSON.parse(JSON.stringify(v.spec)));
    setPickedOriginal(false); // 좌표·식을 골랐다 — 사진으로 바꾸기와 동시에 성립하지 않는다
    setPickedCrop(false);
    setPickedContentVer(null); // 판 그림 고르기와도 동시에 성립하지 않는다 — [저장]은 하나만 보낸다
    setPreviewVer(null); // 이제 draft 가 그 버전이라 미리보기로 겹쳐 볼 것이 없다
    setLoadedVer(version); // 목록의 표시가 이 버전으로 옮겨간다
    // 결과가 있는 자리로 데려간다 — 실제 스크롤은 아래 effect 가 한다(DOM 이 갱신된 뒤여야 한다).
    setScrollToEdit((n) => n + 1);
    setToast({
      type: 'info',
      text: `v${version} 의 값을 가져왔습니다 — 여기서 더 고칠 수 있고, [저장]을 눌러야 반영됩니다.`,
      issues: [],
    });
  };

  /**
   * **사진(보관 원본)으로 바꾸기를 고른다** — 서버에 아무것도 보내지 않는다.
   *
   * 좌표·식 버전의 [이 버전 가져오기](loadVersion)와 **같은 급의 동작**이다: 고르기는 화면 안의
   * 일이고 서버로 나가는 것은 [저장] 하나뿐이다(오너 원칙 2026-09-01). 종전에는 이 자리가
   * [이 원본으로 되돌리기]라는 이름으로 **즉시 PATCH 를 보냈고**, 그래서 「무엇이 되는지 보고
   * 정하기」가 성립하지 않았다.
   *
   * 이력의 사진 버전에서 골라도 여기로 온다 — 돌아갈 곳이 그 한 장뿐이라(계약 §4-2) 어느 줄에서
   * 골랐든 나가는 것은 `PATCH {spec:null}` 하나다. `version` 은 **표시용**이다(목록의 어느 줄에서
   * 골랐는지 배지로 보인다).
   */
  const pickOriginal = (version = null) => {
    if (!writable || anyBusy || !caps.canDropSpec) return;
    setDraft(null);
    setPickedOriginal(true);
    setPickedCrop(false);
    setPickedContentVer(null); // 판 그림 고르기와 동시에 성립하지 않는다 — [저장]은 하나만 보낸다
    setPreviewVer(null);
    setLoadedVer(version == null ? null : version);
    setScrollToEdit((n) => n + 1);
    setToast({
      type: 'info',
      text: '사진으로 바꾸기를 골랐습니다 — 좌표·식은 [저장]을 눌러야 버려집니다. 그 전에는 아무것도 나가지 않습니다.',
      issues: [],
    });
  };

  /**
   * **판 그림(has_content)으로 바꾸기를 고른다** — 서버에 아무것도 보내지 않는다.
   *
   * pickOriginal·loadVersion 과 같은 급의 화면 내 고르기다(오너 원칙 2026-09-01). 다른 점은
   * [저장]이 내보내는 것 하나다 — 이 판의 내용을 싣는 PATCH 가 계약에 없어 restore 1회로 나간다
   * (doRestore 주석). 사진(보관 원본) 판과 갈라 두는 이유: 그쪽은 `PATCH {spec:null}` 이 정본이고,
   * 전부 restore 로 돌리면 caps.canDropSpec 잠금·no_original 409 처리가 어긋난다.
   */
  const pickContentVersion = (version) => {
    if (!writable || anyBusy) return;
    const v = (hist.versions || []).find((x) => Number(x.version) === Number(version));
    // 계약이 정한 갈래 그대로만 받는다 — spec 있는 판은 loadVersion, 내용 없는 판은 pickOriginal 의 몫이다.
    if (!v || v.spec != null || v.has_content !== true) return;
    setDraft(null);
    setPickedOriginal(false);
    setPickedCrop(false);
    setPickedContentVer(Number(version));
    setPreviewVer(null);
    setLoadedVer(version); // 목록의 「고름 · 저장 전」 표시가 이 줄로 옮겨간다(표시용)
    setScrollToEdit((n) => n + 1);
    setToast({
      type: 'info',
      text: `v${version} 의 그림으로 바꾸기를 골랐습니다 — [저장]을 눌러야 서버에 나갑니다. 그 전에는 아무것도 나가지 않습니다.`,
      issues: [],
    });
  };

  /** 고른 것을 무른다 — 서버에 저장된 명세로 폼을 되돌린다. */
  const cancelPick = () => {
    setPickedOriginal(false);
    setPickedCrop(false);
    setPickedContentVer(null);
    setLoadedVer(null);
    setDraft(item?.spec ? JSON.parse(JSON.stringify(item.spec)) : null);
    setToast(null);
  };

  /**
   * 그림 하나를 지운다 — DELETE (계약 §8 · 회신 §2 Q8).
   * **settle 을 타지 않는다.** 삭제 응답에는 `item` 이 없어서 settle 을 그대로 쓰면
   * ① 지워진 그림이 좌·우 칸에 계속 그려지고 ② `index` prop 은 이미 **한 칸 당겨져 온 다른
   * 그림**을 가리키는데 창은 열린 채로 남으며 ③ 비워 둔 이력을 다시 읽으면 그 그림 이력은
   * 퇴장했으므로 404 가 온다. 창을 열어 둘 근거가 없다 — 곧바로 닫고, 호스트가 상세를 다시
   * 읽으며 본문 편집기가 서버 최신 본문으로 리마운트되는 기존 경로에 맡긴다.
   * 응답의 `remaining` 은 **쓰지 않는다** — 그 field 의 남은 수인지 문제 전체인지 계약에 없다.
   * 모르는 수를 화면 문구로 만들지 않는다.
   * 창을 닫는 것은 finally 뒤로 미룬다 — 닫으면 호스트가 이 컴포넌트를 언마운트하므로,
   * 그 뒤에 setBusy 를 부르면 사라진 컴포넌트에 상태를 쓰게 된다.
   */
  const doDelete = async () => {
    setBusy('delete');
    setToast(null);
    let deleted = false;
    let droppedEdited = false;
    try {
      const d = await deleteFigure(target, problemId, field, serverIndex, { expectedUpdatedAt: token }, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      deleted = true;
      droppedEdited = readEditedCopyDropped(d);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
      setDelOpen(false);
    }
    // field 를 함께 실어 보낸다 — 호스트가 「본문/풀이에서 지웠습니다」를 말할 수 있어야 한다.
    // 여기서 토스트를 내지 않는 이유는 이 창이 곧 사라져 아무도 못 보기 때문이다.
    //
    // `editedCopyDropped` 도 같은 이유로 호스트가 받는다(계약 §10) — 편집본이 버려졌다는 통지를
    // 낼 자리가 이 창에는 없다. 다른 쓰기는 창에 남아 띠로 말하지만(editedDropped), 삭제만은
    // 창이 사라진다.
    if (deleted) closeRef.current?.(true, { deleted: true, field, editedCopyDropped: droppedEdited });
  };

  // 좌측 「원본」 칸 — 값·이름·빈칸 문구가 한 벌로 온다(figureText.figureOriginalPane).
  // 2026-08-29 회신 §2 Q7 로 은행에도 `original_src`(변환 전 픽셀 원본)가 실리기 시작했다.
  // 이름을 값과 갈라 두면 반드시 어긋나므로 규칙을 이 창에 박지 않고 한 함수에 모아 시험으로 덮는다.
  const left = figureOriginalPane(item, target);
  /**
   * 교재 크롭(OCR 당시 지면) — 두 대상이 같은 값을 다른 키로 준다(figurePageCrop). 2026-09-06 까지는 `target !== 'ocr'`
   * 로 은행에만 열려 있었다 — 「OCR 은 크롭이 곧 원본이라 [이 사진으로 바꾸기]와 같은 일」이라는 전제였는데, 그 전제는
   * 손대지 않은 bbox 블록에서만 참이다(등록·교체 뒤에는 spec:null 이 올린 이미지로 돌아가고 픽셀이면 400 이라 크롭으로
   * 갈 길이 없었다 · 감사 G1). 서버는 OCR 도 `PATCH {image}` 를 받는다(회신 09-04 §3) — 화면 게이트였다.
   *
   * 두 자리 중 하나에 선다 —
   *   · 좌측 「원본」 칸이 **크롭을 그리고 있으면**(left.kind === 'crop' — 보관 원본을 못 받은 OCR·은행) 그 칸의 버튼이
   *     [교재 크롭으로 바꾸기]다. 이미 그 크롭 그대로인 픽셀 그림에서는 잠근다(바꿀 것이 없다).
   *   · 칸이 보관 원본·픽셀 자체를 그리고 있으면 크롭이 **다른 그림일 때만** 아래 카드로 따로 선다.
   * 규격(png·jpeg·webp · 2MB)은 고를 때 본다 — [저장]에서 413 을 맞게 두지 않는다.
   */
  const pageCrop = figurePageCrop(item, target);
  const cropSendable = !!pageCrop && parseImageDataUri(pageCrop).ok;
  const paneIsCrop = left.kind === 'crop';
  const cropAlready = paneIsCrop && item?.kind === 'pixel' && item?.src === pageCrop;
  const cropPickable = cropSendable && (paneIsCrop ? !cropAlready : pageCrop !== left.src);

  /**
   * **교재 크롭으로 바꾸기를 고른다** — 서버에 아무것도 보내지 않는다(오너 원칙 2026-09-01 「고르기는 화면 안,
   * 나가는 것은 [저장] 하나」). 사진(보관 원본)과 갈래가 다르다 — 그쪽은 `PATCH {spec:null}`(보관본 복귀),
   * 이쪽은 `PATCH {image: origin_crop}`(이력 950, FE-20260831 §3-3 의 선호안 그대로). 명세가 없는 픽셀 그림에서도
   * 된다 — 교체는 kind 를 가리지 않는다.
   */
  const pickCrop = () => {
    if (!writable || anyBusy || !cropPickable) return;
    setDraft(null);
    setPickedOriginal(false);
    setPickedContentVer(null);
    setPickedCrop(true);
    setPreviewVer(null);
    setLoadedVer(null);
    setScrollToEdit((n) => n + 1);
    setToast({
      type: 'info',
      text: '교재 크롭으로 바꾸기를 골랐습니다 — [저장]을 눌러야 서버에 나갑니다. 그 전에는 아무것도 나가지 않습니다.',
      issues: [],
    });
  };

  /** [저장]이 교재 크롭을 내보낸다 — 그 자리 그림이 OCR 당시 지면 사진(픽셀)이 되고 이력에 950 판이 남는다. */
  const doReplaceWithCrop = async () => {
    if (!writable || anyBusy || !cropPickable) return;
    setBusy('save');
    setToast(null);
    try {
      const d = await replaceFigureImage(
        target, problemId, field, serverIndex,
        { image: pageCrop, expectedUpdatedAt: token }, getAccessToken,
      );
      if (d === AUTH_REDIRECT) return;
      settle(d, '교재 크롭 사진으로 바꿨습니다.');
    } catch (e) { fail(e); } finally { setBusy(null); }
  };

  // 저장할 수 있는가 — 렌더가 통과했고(§④), 그릴 원소가 있고, 설명이 있고, 쓰기가 열려 있을 때
  const altOk = typeof pruned?.alt === 'string' && pruned.alt.trim() !== '';
  // 미리보기 중에는 저장을 막는다 — 화면이 그리는 것은 **고른 옛 버전**이고 저장될 것은 편집 중인 값이라,
  // 그대로 두면 「보이는 것을 저장한다」는 화면의 약속이 깨진다(2026-08-30).
  const canSave = writable && !anyBusy && previewVer == null
    // 교재 크롭으로 바꾸기 — 그릴 명세가 없고 보관 원본과도 무관하다. 규격은 고를 때 이미 봤다(cropPickable).
    && (pickedCrop ? cropPickable : pickedOriginal
      // 사진으로 바꾸기 — 그릴 명세가 없으니 렌더가 통과할 일이 없다. 서버가 받아 주는가만 본다
      // (이미 픽셀인 그림에 `spec:null` 은 400, 보관 원본이 없으면 409 · 계약 §4-2).
      ? caps.canDropSpec
      // 판 그림으로 바꾸기 — 같은 이유로 렌더 통과를 우회한다. 보관 원본과도 무관하다(restore 는
      // 서버가 제 스냅샷을 되살린다 · 계약 「판 내용 스냅샷」) — canDropSpec 에 묶지 않는다.
      : pickedContentVer != null
        || (preview?.ok === true && !rendering && hasDrawable(pruned) && altOk));
  /**
   * 「사진으로 바꾸기」가 실제로 그릴 수 있는 한 장 — 받아 온 원본일 때만이다.
   * `left.src` 는 원본을 못 받았을 때 **지금 본문의 그림**으로 물러나므로(figureOriginalPane),
   * 그것을 그대로 쓰면 「이 사진이 됩니다」가 거짓이 된다. 그래서 state 로 한 번 거른다.
   */
  const originalPickSrc = left.state === 'ok' ? left.src : null;

  /**
   * **화면이 지금 그리고 있는 그 그림** — 크기까지 한 벌로 쥔다.
   *
   * 쓰는 자리가 둘이고, 둘이 같아야 한다 —
   *   ① 미리보기 칸이 아직 그릴 것이 없을 때 이 그림을 띄운다(창을 열면 바로 보인다).
   *   ② [AI 요청]이 구워 보내는 것도 이 값이다.
   *
   * ⚠ **미리보기가 있으면 그쪽이 먼저다**(2026-09-01 수정). 종전에는 `item`(서버 저장본)만
   * 보고 있어서, 이력에서 [이 버전 가져오기]로 v3 을 불러왔거나 숫자·식을 고쳐 둔 상태에서
   * **화면은 v3 을 그리는데 서버로는 저장본이 나갔다.** [AI 요청]의 잠금은 `previewVer` 만
   * 보고 `loadedVer`·`specDirty` 는 보지 않아 그대로 눌렸고, 그래서 「가져온 버전으로 다시
   * 그려 달라」가 조용히 「지금 저장된 것으로 다시 그려 달라」가 됐다. 바로 아래 AI 창에
   * 걸어 둔 약속(「화면이 그리는 것과 보내는 것이 다르면 거짓이 된다」)이 이 경로에서 깨져 있었다.
   *
   * 크기를 함께 쥐는 이유: figureImage 는 `naturalWidth || width` 가 0 이면 굽기를 거절한다.
   * 그림만 바꾸고 크기를 저장본 것으로 두면 엉뚱한 배율로 구워진다.
   */
  // previewingContent 도 가른다 — 훅이 꺼져도 옛 렌더 결과를 쥐고 있으므로(useSpecRender), 판 그림을
  // 보는 동안 그 낡은 SVG 를 「지금 그림」이라 부르면 화면과 보내는 것이 갈린다. (그 상태의 쓰기
  // 버튼은 아래에서 전부 잠그지만, 이 값은 미리보기 칸의 폴백으로도 그려진다.)
  const sendNow = FIGURE_SVG_EDITOR ? { src: item?.src || nodeSrc, width: item?.width, height: item?.height } : (!previewingOriginal && !previewingContent && preview?.ok && preview.svg)
    ? { src: preview.svg, width: preview.width, height: preview.height }
    : { src: item?.svg || item?.src || nodeSrc || null, width: item?.width, height: item?.height };
  const nowSrc = sendNow.src;

  /**
   * 그림 속 글자의 순서 — **서버가 준 차례를 그대로 쓴다**(t1·t2…). 편집 중인 traceDraft 의
   * 키 순서로 그리면 사람이 고칠 때마다 줄이 뒤바뀔 수 있다(객체 키 순서는 약속이 아니다).
   *
   * OCR 도 센다(2026-09-05 · 계약 §10-2 G3·G4) — OCR 재요청이 trace 를 받고 `PATCH {trace_texts}` 도 열려,
   * 종전에 「은행 재요청만」이라 걸어 두었던 `target === 'ocr' ? []` 가드를 걷었다. 두 대상이 같은 규칙이다.
   */
  const traceIds = useMemo(() => readTraceTexts(item).map((t) => t.id), [item]);
  /**
   * **사진을 따라 그린 그림인데 고칠 좌표·식이 없는 상태.**
   *
   * 이 그림은 `kind:'svg'` 인데 `spec:null` 이라(실측 61654) 창의 오래된 가정 —「svg 면 고칠
   * 명세가 있다」— 이 깨진다. 그대로 두면 빈 폼이 열린 채로 남고, 잠긴 [저장]의 안내가
   * 「그림 설명을 채워야…」라서 사람을 `PATCH {spec}` 로 안내한다(트레이스가 통째로 갈린다).
   *
   * 이력에서 좌표·식 버전을 가져오면(loadVersion) draft 가 생기므로 이 상태가 풀린다 —
   * 그때는 폼이 다시 열려야 한다. 그래서 `item.spec` 이 아니라 **draft** 를 본다.
   */
  const traceNoSpec = traceIds.length > 0 && draft == null;

  /**
   * 고치는 칸은 **하나**다 — 그 안에서 무엇을 고칠지만 갈린다 (2026-09-01, 오너 지시 「합쳐 줄래」).
   *
   * 종전에는 「숫자·식」 칸과 「그림 속 글자」 상자가 따로 섰다. 트레이스 그림에서는 앞칸이
   * 「여기엔 고칠 것이 없습니다」만 말하고 실제로 할 수 있는 일은 아래 다른 상자에 있어서,
   * 사람이 두 자리를 오가야 했고 화면 높이도 두 배로 먹었다.
   *
   * 어느 쪽이 **설 수 있는가**는 그림이 정한다:
   *   · 좌표·식 — 고칠 명세가 있을 때(트레이스인데 명세가 없으면 폼을 내지 않는다 → traceNoSpec)
   *   · 그림 속 글자 — 그림 안에 글자 목록이 있을 때(트레이스 그림)
   * 둘 다 설 수 있는 그림(명세도 있고 글자도 있는 판)에서만 **탭이 보인다.** 하나뿐이면
   * 탭 없이 그 이름이 칸 제목이 된다 — 고를 것이 하나인데 고르라고 내밀지 않는다.
   */
  // 기본은 글자다 — 이 칸에 글자가 뜬다는 것 자체가 사진을 따라 그린 그림이라는 뜻이고,
  // 그 그림을 여는 이유는 대개 글자 고치기다(계약의 실사례 61654 「5명 → 6명」).
  const [editTab, setEditTab] = useState('trace');
  // 판정은 figureText 한 곳이 갖는다 — 여기서 다시 재면 칸 제목·머리줄 버튼·본문이 어긋난다.
  const editTabs = figureEditTabs({ traceCount: traceIds.length, hasSpec: draft != null, picked: editTab });
  const activeEditTab = editTabs.active;
  const bothEditTabs = editTabs.showTabs;

  const busyReconvert = busy === 'reconvert';
  const busyDelete = busy === 'delete';
  // 응답을 못 받고 닫히면 화면이 무슨 일이 났는지 말할 수 없다 — 재요청과 같은 이유로 삭제도 막는다.
  const closeBlocked = FIGURE_SVG_EDITOR ? anyBusy : busyReconvert || busyDelete;

  return (
    <Modal
      isOpen={open}
      onClose={() => { if (!closeBlocked) closeRef.current?.(changed); }}
      title={ACTION_TITLE[action] || ACTION_TITLE.edit}
      size={deleting ? 'lg' : 'max'}
      /* §② 닫힘 차단 — 배경 클릭은 항상 끄고, ESC 는 100초 요청 중에만 끈다 */
      closeOnBackdropClick={false}
      closeOnEsc={!closeBlocked && !aiOpen && !delOpen}
      showCloseButton={!closeBlocked}
      /* 값의 출처는 src/styles/z-index.css 의 --z-modal-figure 다. Modal 이 숫자 prop 을 받아
         인라인 스타일로 쓰므로 CSS 변수를 그대로 넘길 수 없어, 공용 Modal 의 기본값(9999980)과
         같은 방식으로 숫자를 적는다. 두 값을 함께 고쳐야 한다. */
      zIndex={9999985}
    >
      <div className="space-y-3">
        <p
          role={toast?.type === 'error' ? 'alert' : 'status'}
          aria-live={toast?.type === 'error' ? 'assertive' : 'polite'}
          className={`text-xs ${toast ? (toast.type === 'error' ? 'text-danger' : 'text-gray-600') : 'sr-only'}`}
        >
          {toast?.text || ''}
        </p>
        {/* 사유 줄은 **붉게** 낸다(오너 지시 2026-09-01). 위 토스트 문장은 회색으로 둔다 —
            「본문은 그대로다(손실 없음)」와 「이래서 못 옮겼다」는 다른 말이고, 둘을 같은 색으로
            묶으면 사유가 안내문에 묻혀 왜 그림이 없는지 아무도 안 읽는다. 색은 danger 토큰만 쓴다. */}
        {toast?.issues?.length > 0 && (
          <ul className="list-disc space-y-0.5 rounded-lg bg-gray-50 py-2 pl-6 pr-3 text-[11px] text-danger">
            {toast.issues.map((s, i) => <li key={`${i}-${String(s).slice(0, 12)}`}>{String(s)}</li>)}
          </ul>
        )}

        {/* 서버가 바로잡은 곳(계약 §9-4 `corrections[]`). issues(못 옮긴 것)와 **다른 것**이라
            자리를 가른다 — 하나는 「빠졌다」, 하나는 「고쳤다」이고 사람이 할 일이 다르다.
            문자열이 아니면 readCorrections 가 걸러 빈 목록이 되고, 그러면 아무것도 안 그린다. */}
        {!FIGURE_SVG_EDITOR && corrections.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <p className="text-[11px] font-medium text-gray-600">{FIGURE_CORRECTIONS_LABEL}</p>
            <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-[11px] text-gray-500">
              {corrections.map((c, i) => <li key={`${i}-${c.slice(0, 12)}`}>{c}</li>)}
            </ul>
          </div>
        )}

        {/* 서버가 알려 준 남은 길(계약 §9-4 `suggest:"trace"`). 토스트가 이미 말로 했지만,
            **누를 것이 없으면 말뿐이다** — 그 길로 데려가는 버튼을 같은 자리에 둔다.
            누르면 재요청 창이 [사진 따라 그리기]가 골라진 채로 열린다. */}
        {!FIGURE_SVG_EDITOR && suggestTrace && !anyBusy && writable && (
          <Button size="xs" variant="primary-outline" icon={<Wand2 className="size-3.5" />}
            title={FIGURE_MODE_TEXT.trace.hint}
            onClick={() => { setAiMode('trace'); setAiOpen(true); }}>
            {FIGURE_MODE_TEXT.trace.name}로 해보기
          </Button>
        )}

        {loading && <div className="h-64 rounded-xl bg-gray-100 motion-safe:animate-pulse" />}

        {!loading && loadError && (
          <div className="space-y-2 p-3">
            <p role="alert" className="text-sm text-danger">{loadError}</p>
            <Button size="xs" variant="secondary" onClick={load}>다시 시도</Button>
          </div>
        )}

        {/* **이미 일어난 손실**이라 다른 어떤 띠보다 위에 둔다(계약 §10 · 회신 §2).
            이 띠는 로딩·오류와 무관하게 낸다 — 창을 다시 읽는 중이라고 해서 방금 잃은 문장이
            돌아오지는 않고, 그 사이 띠가 사라지면 사람은 통지를 놓친다. */}
        {editedDropped && (
          <p role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            {FIGURE_EDITED_DROPPED_TEXT}
          </p>
        )}

        {/* 편집본이 원인이면 그것부터 말한다 — 개수가 어긋났든 바이트가 어긋났든 할 일이 하나다.
            이 띠가 뜬 창은 쓰기가 잠긴다(editedMismatch → writable false). 회신 §2 Q3. */}
        {!loading && !loadError && editedMismatch && (
          <p role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            {describeFigureEditedCopy(field)}
          </p>
        )}

        {/* ① **누른 것이 서버가 모르는 그림**일 때 — 가장 구체적인 원인이라 다른 어떤 대조 문구보다
            먼저 낸다. 이 그림에는 어떤 쓰기도 걸 수 없고, 대신 빼는 길을 같은 자리에 둔다.
            버튼이 없으면 문구가 [편집기] 탭을 부른다(describeUnknownFigure 의 canRemove). */}
        {!loading && !loadError && !editedMismatch && clickedUnknown && (
          <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <p role="alert" className="text-xs text-amber-800">
              {describeUnknownFigure({
                field, target, canRemove: canRemoveNode, canRegister: canRegisterNode && pastedCheck.ok,
              })}
            </p>
            {/* 이미지가 규격을 벗어나면 등록이 확정 실패다 — 그때는 버튼을 내지 않고 이유를 말한다 */}
            {canRegisterNode && !pastedCheck.ok && (
              <p className="text-[11px] text-amber-700">{describeImageReject(pastedCheck)}</p>
            )}
            <div className="flex flex-wrap gap-2">
              {canRegisterNode && pastedCheck.ok && (
                <Button size="xs" variant="primary" icon={<Plus className="size-3.5" />}
                  loading={busy === 'register'} disabled={anyBusy} onClick={doRegisterNode}>
                  새 그림으로 등록
                </Button>
              )}
              {canRemoveNode && (
                <Button size="xs" variant="secondary" icon={<Trash2 className="size-3.5" />}
                  disabled={anyBusy} onClick={doRemoveNode}>
                  본문에서 빼기
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ② 같은 그림이 둘이라 자리를 못 가른다 — 막는 이유가 다르므로 문구도 다르다. */}
        {!loading && !loadError && !editedMismatch && !clickedUnknown && clickedDuplicate && (
          <p role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            {describeDuplicateFigure(field)}
          </p>
        )}

        {/* ③ 줄은 어긋났지만 **누른 그림은 바이트로 찾아** 쓰기가 열린 경우 — 경고가 아니라 고지다.
            빨간 말로 겁을 주면 사람이 멀쩡히 되는 편집을 피한다. 대신 저장의 대가(편집본 폐기)를 말한다. */}
        {!loading && !loadError && !editedMismatch && extraImagesNote && (
          <p className="rounded-xl border border-gray-200 bg-gray-50 p-2.5 text-[11px] leading-relaxed text-gray-600">
            {describeExtraImagesNote(extraImages, field)}
          </p>
        )}

        {/* ③′ 서버가 더 많다 — 누른 그림은 찾았고 쓰기도 열려 있다. 「막았습니다」가 아니라 고지다. */}
        {!loading && !loadError && !editedMismatch && serverExtra > 0 && (
          <p className="rounded-xl border border-gray-200 bg-gray-50 p-2.5 text-[11px] leading-relaxed text-gray-600">
            {describeServerExtraNote(serverExtra, field)}
          </p>
        )}

        {/* ④ 그 밖의 줄 대조 실패 — **쓰기가 실제로 잠긴 경우만**. 바이트로 찾아 쓰기가 열린 창에서
            「막았습니다」라고 말하면 화면이 거짓말을 한다(2026-09-04 실측). item 유무보다 **먼저**
            말한다 — 개수가 어긋나면 그 순번의 그림이 아예 없을 수도 있는데, 그때 「찾지 못했습니다」보다
            「몇 장 대 몇 장」이 원인을 짚는다. */}
        {!loading && !loadError && !editedMismatch && match?.seq?.ok === false && !writable
          && !clickedUnknown && !clickedDuplicate && !extraImagesNote && (
          <p role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            {describeFigureMismatch(match.seq, target)}
          </p>
        )}

        {!loading && !loadError && !item && match?.seq?.ok !== false && !clickedDuplicate && (
          <p role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            {describeUnknownFigure({ field, target, canRemove: canRemoveNode && match?.bySrc?.reason === 'missing' })}
          </p>
        )}

        {/* 서버가 거절해 잠긴 창(match.fromError)에는 이 띠를 내지 않는다 — 원인은 위 토스트가 이미
            정확히 말했고, 여기서 대조 문구를 겹쳐 내면 한 화면이 두 말을 한다(409 stale 에서
            「창을 닫으라」와 「저장하라」가 동시에 떴다). 대조 실패일 때만 대조를 말한다. */}
        {!loading && !loadError && item && !editedMismatch
          && match?.ok === false && !match?.fromError && match?.seq?.ok !== false && (
          <p role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            {/* 단건 실패에도 사유를 실어 보낸다 — 'kind'(종류가 다르다)와 'missing'(그 자리에 없다)은
                사용자가 할 일이 다르다. at 은 그 field 안의 순번이라 index 와 같은 값이다. */}
            {describeFigureMismatch(isCompareReason(match.reason) ? { reason: match.reason, at: Number(index) } : null)}
          </p>
        )}

        {/* 통과했지만 완전히 같지는 않은 그림 — **막지 않고 사실만 밝힌다.**
            · exact === null : 서버가 대조용 src 를 주지 않았다(회신 Q3 「대조 대상 아님」)
            · exact === false: 바이트가 다르다. 2026-08-30 정정 전에는 이것을 막았고 그 판정이
              정상 그림까지 잠갔다 — 순번은 개수·종류가 지키므로 여기서는 알리기만 한다. */}
        {!loading && !loadError && item && !editedMismatch && match?.ok
          && (match.exact === null || match.exact === false) && (
          <p className="rounded-xl border border-gray-200 bg-gray-50 p-2.5 text-[11px] text-gray-500">
            {/* 사유가 셋이라 문구도 셋이다. `afterWrite` 를 먼저 본다 — 그 경우 서버는 멀쩡하고
                낡은 쪽이 **내 화면**이라, 「서버가 대조용 원본을 주지 않아…」는 거짓이 된다. */}
            {match.afterWrite ? FIGURE_AFTER_WRITE_TEXT
              : match.exact === null ? FIGURE_NO_COMPARE_TEXT : FIGURE_INEXACT_TEXT}
          </p>
        )}

        {!loading && !loadError && item && (
          <>
            {/* 저장된 명세가 무효한 그림 — 안내만 하고 저장을 막지는 않는다(§④) */}
            {!FIGURE_SVG_EDITOR && Array.isArray(item.issues) && item.issues.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-2.5">
                <p className="text-[11px] font-medium text-amber-800">지금 저장된 명세가 무효라 원본 그림으로 그려지고 있습니다 — 아래에서 고칠 수 있습니다.</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[11px] text-amber-700">
                  {item.issues.map((s, i) => <li key={`${i}-${String(s).slice(0, 12)}`}>{String(s)}</li>)}
                </ul>
              </div>
            )}

            {/**
              * ── 칸이 셋이다: **원본 · 미리보기 · 고치는 칸** ──
              *
              * 2026-09-01 에는 원본 칸을 걷었다(오너 지시 「이미지 편집 모달에서는 이미지 자체를 수정하는
              * 기능으로 원복시키고, 이미지는 두 개가 아니라 하나만」). 그 지시가 막은 것은 **미리보기가 둘로
              * 갈려 「지금 [저장]을 누르면 무엇이 되는가」가 흐려진 것**이었고, 그 답은 지금도 가운데 칸 하나다.
              *
              * 2026-09-06 오너 지시로 원본이 **왼쪽 칸으로 돌아왔다** — 「OCR 당시의 원본은 표출하는 것부터
              * 우선 작업해줘(지금 안 보임)」. 걷어 둔 사이 원본을 보는 길은 [이미지 교체]를 펴야 나오는 80px
              * 썸네일뿐이었다. 창을 열면 좌표·식 폼만 서고, 원본이 **있는지조차** 화면이 말하지 않았다.
              * 이 칸은 고치지 않는다 — 대조만 한다. 그래서 09-01 의 물음(무엇이 되는가)과 다투지 않는다.
              *
              * 폭 240px 은 실측으로 정했다(창 1400px · 실측 2026-09-06): 종전 2단은 미리보기 766 / 고치는 칸 590
              * 이었고, 3단으로 나눠도 도판은 `maxWidth: preview.width`(이 그림 310px)라 **줄어들지 않는다.**
              * 240px 은 썸네일(80px)의 세 배이고, 교재 크롭 실물(대개 300px 안팎)이 거의 원 크기로 선다.
              */}
            {FIGURE_SVG_EDITOR && !deleting ? (
              <SvgFigureWorkspace target={target} problemId={problemId} field={field}
                item={item} token={token} writable={writable} busy={busy} setBusy={setBusy}
                getAccessToken={getAccessToken} onSettled={settle} externalError={svgFailure}
                cropSrc={cropPickable ? pageCrop : null} initialHistory={faces.opensPicker}
                onRequestAi={() => { setAiMode('faithful'); setAiOpen(true); }} />
            ) : (<>
            {faces.editPanes ? (
              <div ref={editAreaRef} className="grid gap-3 lg:grid-cols-[240px_minmax(0,1.3fr)_minmax(0,1fr)]">
                {/* 원본 — 사진이 서면 그 사진의 이름, 아니면 **없다는 사실**을 이름으로 말한다(figureOriginPaneLabel).
                    값·이름·빈칸 문구는 [이미지 교체] 칸의 카드와 **같은 함수**에서 나온다(figureOriginalPane) —
                    두 자리가 다른 말을 하면 어느 쪽이 참인지 화면이 스스로 답하지 못한다. */}
                <Pane height="h-80" label={figureOriginPaneLabel(left)}>
                  {left.src && (left.kind === 'stored' || left.kind === 'crop')
                    ? <img src={left.src} alt={left.label} className="size-full object-contain" />
                    : <Empty text={figureOriginPaneNote(left)} />}
                </Pane>
                <Pane
                  height="h-80"
                  label={previewingContent
                    /* 판 그림(has_content) — 이 칸의 답(「저장하면 무엇이 되는가」)이 원본도
                       좌표·식도 아니라 **그 판의 실제 그림**일 때다(계약 「판 내용 스냅샷」). */
                    ? (previewVersion
                      ? `이력 v${previewVer} — 이 판의 실제 그림, 저장되지 않았습니다`
                      : `이력 v${pickedContentVer} 의 그림으로 바꾸기를 골랐습니다 — 저장되지 않았습니다`)
                    : previewingOriginal
                      ? (previewVersion
                        ? `이력 v${previewVer} — 고르면 이 사진이 됩니다`
                        : '사진으로 바꾸기를 골랐습니다 — 저장되지 않았습니다')
                      : previewSpec
                        ? `이력 v${previewVer} 을 그려 본 것 — 저장되지 않았습니다`
                        /* 첫 렌더가 오기 전과 좌표·식이 아예 없는 사진 그림에서는 이 칸이 **지금
                           그림**을 그린다 — 그때 「미리보기」라고 부르면 고친 값이 반영된 것으로
                           읽힌다. 훅은 새 요청 중에도 옛 결과를 쥐고 있으므로, preview 가 null 인
                           것은 정확히 그 두 경우다(useSpecRender). */
                        : preview
                          /* 「값을 고치면 바로 바뀝니다」를 걷고 그 자리에 **손으로 옮기기가 열리지 않는 까닭**을
                             넣었다(오너 지시 2026-09-06). 종전에는 그 말이 그림 위에 겹쳐 떠 도판을 가렸다.
                             고치면 바뀌는 것은 눌러 보면 아는 일이고, 손잡이가 없는 까닭은 말하지 않으면 모른다.
                             긴 문장은 이 줄을 밀어내므로 자르고 title 로 전문을 준다(Pane 이 min-w-0 truncate). */
                          ? (dragNote
                            ? <span title={dragNote}>미리보기 — <span className="text-amber-700">{dragNote}</span></span>
                            : '미리보기')
                          : '지금 그림 — 값을 고치면 여기서 바뀝니다'}
                  busy={rendering}
                  right={!previewingOriginal && !previewingContent && preview?.ok && preview.svg ? (
                    <div className="flex items-center gap-1.5">
                      <button type="button" onClick={() => setDragOn((v) => !v)} disabled={!writable || anyBusy}
                        aria-pressed={dragOn}
                        className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${dragOn ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} disabled:opacity-40`}>
                        <Move className="size-3" />손으로 옮기기
                      </button>
                      {dragOn && (
                        <select value={snapStep} onChange={(e) => setSnapStep(Number(e.target.value))}
                          aria-label="눈금 간격"
                          className="rounded border border-gray-200 py-0.5 pl-1 pr-4 text-[11px] text-gray-600">
                          <option value={1}>눈금 1</option>
                          <option value={0.5}>눈금 0.5</option>
                          <option value={0.1}>눈금 0.1</option>
                          <option value={0.01}>눈금 0.01</option>
                          <option value={0.001}>눈금 0.001</option>
                          <option value={0}>눈금 없음</option>
                        </select>
                      )}
                    </div>
                  ) : null}
                >
                  {/* 판 그림(has_content) — GET history/{version} 의 src(그 판의 실제 내용)를
                      그린다. 실패해도 이 칸만 그 사실을 말한다(목록은 산다 — 그 판만 폴백).
                      실패 시 보관 원본으로 물러나지 **않는다** — [저장](restore)이 되살리는 것은
                      원본이 아니라 그 판의 내용이라, 원본을 그리면 이 칸의 약속이 거짓이 된다. */}
                  {previewingContent && (
                    contentEntry && contentEntry !== 'loading'
                      ? <img src={contentEntry.src} alt={contentEntry.alt || `v${contentVer} 그림`} className="size-full object-contain" />
                      : contentEntry === null
                        ? (
                          /* [보기] 토글은 contentVer 를 안 바꿔 재시도가 안 탄다 — 버튼이 논스를 올린다.
                             [저장]=restore 문장은 골라 둔 상태에서만 참이다(보기만일 때 [저장]은 잠겨 있거나 PATCH 다). */
                          <div className="flex flex-col items-center gap-2 p-3 text-center">
                            <p className="text-xs text-gray-500">
                              이 판의 그림을 서버에서 받지 못했습니다.
                              {pickedContentVer != null ? ' [저장]은 화면과 무관하게 이 판의 내용으로 되돌립니다.' : ''}
                            </p>
                            <Button size="xs" variant="secondary" onClick={() => setVerRetry((n) => n + 1)}>
                              다시 시도
                            </Button>
                          </div>
                        )
                        : <Empty text="이 판의 그림을 받는 중…" />
                  )}
                  {/* 사진을 고른 상태 — 서버가 보관한 그 한 장을 그린다. 이 칸의 약속은 「저장하면
                      무엇이 되는가」이고, 그 답이 좌표·식이 아니라 사진일 때가 이때다(계약 §4-2).
                      left.src 를 쓰지 않는 이유: 보관 원본이 없으면 그 값은 지금 본문의 그림으로
                      물러나므로(figureOriginalPane), 그것을 「이 사진이 됩니다」라고 부르면 거짓이 된다. */}
                  {previewingOriginal && (originalPickSrc
                    ? <img src={originalPickSrc} alt={left.label} className="size-full object-contain" />
                    : <Empty text={left.text || '보여 줄 사진이 없습니다.'} />)}
                  {!previewingOriginal && !previewingContent && renderError && <p role="alert" className="p-3 text-xs text-danger">{renderError}</p>}
                  {!previewingOriginal && !previewingContent && !renderError && preview?.ok && preview.svg && (
                    /* 상자를 도판 비율로 고정한다 — 그래야 위에 얹은 손잡이가 그림과 정확히 겹친다.
                       object-contain 으로 두면 여백이 생겨 손잡이가 어긋난다.
                       그래서 상자에 max-height 를 걸지 않는다 — 너비가 정해진 요소에서는 세로 제약이
                       가로로 전이되지 않아 비율이 깨지고, 그러면 손잡이가 그림에서 밀린다.
                       대신 칸(Pane)의 높이를 못 박고 넘치는 만큼은 칸이 스크롤한다. */
                    <div
                      className="relative w-full shrink-0"
                      style={{ aspectRatio: `${preview.width} / ${preview.height}`, maxWidth: `${preview.width}px` }}
                    >
                      <img src={preview.svg} alt={preview.alt || '미리보기'} className="absolute inset-0 size-full" />
                      {dragOn && (
                        <FigDragLayer
                          spec={pruned}
                          /* 판정은 위에서 한 번 했다 — 머리줄 문구와 같은 값이다(dragState 주석). */
                          state={dragState}
                          step={snapStep}
                          disabled={!writable || anyBusy}
                          onChange={setDraft}
                        />
                      )}
                    </div>
                  )}
                  {!previewingOriginal && !previewingContent && !renderError && preview && !preview.ok && (
                    <div className="space-y-1 p-3">
                      <p className="text-xs text-gray-600">이 값으로는 그릴 수 없습니다 — 저장을 잠갔습니다.</p>
                      <ul className="list-disc space-y-0.5 pl-4 text-[11px] text-gray-500">
                        {preview.issues.map((s, i) => <li key={`${i}-${String(s).slice(0, 12)}`}>{String(s)}</li>)}
                      </ul>
                    </div>
                  )}
                  {/**
                    * 아직 그릴 좌표·식이 없거나(사진 그림) 렌더 응답을 기다리는 동안 —
                    * **지금 본문에 실린 그 그림을 그대로 띄운다**(오너 지시 2026-09-01
                    * 「편집 모달이 실행되면 현재 그림을 바로 띄워주고 편집할 수 있도록」).
                    *
                    * 원본 칸을 걷은 뒤로 이 칸이 비면 창에 그림이 **한 장도 없다.** 특히 사진
                    * 그림은 좌표·식이 없어 렌더가 아예 돌지 않으므로, 종전 문구(「값을 고치면 여기
                    * 그려집니다」)만 뜨는 빈 창이 됐다. 고를 순서는 화면 표시 규칙과 같다
                    * (계약 §1-2: `svg ?? src`) — 창이 그리는 것과 [AI 요청]이 보내는 것이 같아야 한다.
                    */}
                  {!previewingOriginal && !previewingContent && !renderError && !preview && (nowSrc
                    ? <img src={nowSrc} alt="지금 그림" className="size-full object-contain" />
                    : (!rendering && <Empty text="값을 고치면 여기 그려집니다." />))}
                </Pane>
                {/**
                  * 고치는 칸 — 「숫자·식」과 「그림 속 글자」가 **한 자리**를 쓴다(2026-09-01).
                  *
                  * 종전에는 이 칸이 「숫자·식」 전용이었고 글자는 아래 별도 상자였다. 사진을 따라
                  * 그린 그림에서는 이 칸이 「여기엔 고칠 것이 없습니다」만 말하고 할 수 있는 일은
                  * 다른 상자에 있어, 한 화면에 칸이 둘인데 쓸모는 하나였다.
                  *
                  * 탭은 **둘 다 설 수 있을 때만** 보인다(bothEditTabs). 라벨·굵기·색은 형제
                  * Pane(원본·미리보기)의 머리줄 규격을 그대로 쓴다 — 같은 층위라 축이 달라지면
                  * 다른 것으로 읽힌다. 아이콘도 형제에 없으므로 여기서만 달지 않는다.
                  */}
                <Pane
                  height="h-80"
                  label={bothEditTabs ? (
                    <span className="flex items-center gap-2" role="tablist" aria-label="무엇을 고칠지">
                      {[['spec', '숫자·식'], ['trace', '그림 속 글자']].map(([key, name]) => (
                        <button
                          key={key}
                          type="button"
                          role="tab"
                          aria-selected={activeEditTab === key}
                          onClick={() => setEditTab(key)}
                          className={`transition-colors ${activeEditTab === key
                            ? 'font-bold text-gray-700'
                            : 'text-gray-400 hover:text-gray-600'}`}
                        >
                          {name}
                        </button>
                      ))}
                    </span>
                  ) : (activeEditTab === 'trace' ? '그림 속 글자' : '숫자·식')}
                  /**
                   * 나가는 중은 **머리줄의 점**이 말한다 — Pane 이 이미 그 자리를 갖고 있다.
                   * 버튼 안에 로딩을 넣으면 글자 폭이 바뀌어 머리줄이 흔들린다.
                   */
                  busy={busy === 'trace'}
                  /**
                   * [글자 저장]은 **글자를 보고 있을 때만** 머리줄에 선다.
                   *
                   * 잠금 조건은 옮기기 전 그대로다 — 화면이 그리는 그림과 나가는 그림이 같을
                   * 때만 눌린다. 이 창은 [저장]·[AI 요청]에 이미 그 약속을 걸어 두었는데 이
                   * 버튼만 빠져 있었다: 이력의 옛 판을 [보기]로 띄운 채 누르면 사람은 다른
                   * 그림을 보면서 **지금 판의 글자**를 내보내게 된다.
                   * 트레이스 그림은 draft 가 null 이라 specDirty 가 구조적으로 거짓이어서,
                   * 다른 관문이 대신 막아 주지도 않는다.
                   *
                   * 모양은 **형제 Pane 의 머리줄 버튼과 같은 것**을 쓴다(위 [손으로 옮기기] —
                   * `rounded-full px-2 py-0.5 text-[11px]` · 아이콘 `size-3` · `disabled:opacity-40`).
                   * 종전 상자에서 쓰던 공용 [Button size=xs] 는 `py-1 text-xs` 라, 그대로 옮기면
                   * 이 칸의 머리줄만 형제보다 높아지고 글자도 한 단 커진다.
                   */
                  right={activeEditTab === 'trace' ? (
                    <button type="button" onClick={doSaveTraceTexts}
                      disabled={!writable || anyBusy || specDirty || previewVer != null || pickedOriginal || pickedCrop
                        || pickedContentVer != null}
                      title={previewVer != null
                        ? `지금은 이력 v${previewVer} 을 보는 중입니다 — 목록의 [보는 중]을 다시 눌러 끄면 글자를 보낼 수 있습니다.`
                        : pickedOriginal
                          ? '사진으로 바꾸기를 골라 둔 상태입니다 — 위 [고르기 취소]를 누르면 글자를 보낼 수 있습니다.'
                          : pickedContentVer != null
                            ? '판 그림으로 바꾸기를 골라 둔 상태입니다 — 위 [고르기 취소]를 누르면 글자를 보낼 수 있습니다.'
                            : specDirty
                              ? '숫자·식에 저장하지 않은 고침이 있습니다 — 위 [가져오기 취소]로 무르거나, [저장]으로 그 값을 반영한 뒤에 눌러 주세요.'
                              : '바뀐 글자만 서버로 보냅니다 — 이력에 버전이 하나 쌓입니다'}
                      className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600 hover:bg-gray-200 disabled:opacity-40">
                      <Save className="size-3" />글자 저장
                    </button>
                  ) : null}
                >
                  <div className="size-full overflow-y-auto p-2">
                    {activeEditTab === 'trace' ? (
                      /**
                       * 그림 속 글자 (계약 §9-4) — 「사진 따라 그리기」로 만든 그림에만 있다.
                       *
                       * 이 칸이 트레이스를 픽셀 대신 쓰는 **이유**다. 사진은 문제를 변형해도 그림 속
                       * 「5명」이 그대로 남지만, 트레이스한 그림은 그 글자가 진짜 `<text>` 라 갈아 끼울
                       * 수 있다(계약의 실사례 — 61654 대관람차 5명 → 6명).
                       *
                       * 목록이 비면 이 탭 자체가 서지 않는다(figureEditTabs 의 canTrace) — 트레이스가 아닌 그림에
                       * 이 칸을 내면 눌러 봐야 409 `not_traced` 다.
                       */
                      <div className="space-y-1.5">
                        <p className="px-1 text-[11px] leading-relaxed text-gray-400">
                          사진을 따라 그릴 때 얹은 글자입니다 — 고치면 그림 안의 글자가 바뀝니다
                        </p>
                        {traceIds.map((id) => (
                          <label key={id} className="flex items-center gap-2">
                            {/* id(t1·t2…)를 그대로 보인다 — 그림 안 어느 글자인지 계약이 자리를 주지 않아
                                화면이 짚어 줄 방법이 없다. 값이 오면 그림 위에 겹쳐 짚는다(→ 확인 §2). */}
                            <span className="w-8 shrink-0 text-[11px] text-gray-400">{id}</span>
                            <input
                              type="text"
                              value={traceDraft[id] ?? ''}
                              onChange={(e) => setTraceDraft((prev) => ({ ...prev, [id]: e.target.value }))}
                              disabled={!writable || anyBusy}
                              className="min-w-0 flex-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-800 focus:border-primary focus:outline-none disabled:bg-gray-50"
                            />
                          </label>
                        ))}
                        {/* 좌표·식 탭이 아예 서지 않는 그림에서만 그 사실을 적는다 — 탭이 둘이면
                            사람이 눌러 볼 수 있으니 「없다」는 말이 거짓이 된다. */}
                        {traceNoSpec && (
                          <p className="px-1 pt-1 text-[11px] leading-relaxed text-gray-500">{FIGURE_TRACE_NO_SPEC_TEXT}</p>
                        )}
                      </div>
                    ) : (
                      /**
                       * 사진을 따라 그린 그림에는 **폼을 내지 않는다**(2026-09-01).
                       *
                       * 고칠 좌표·식이 없는데 빈 폼이 열려 있으면 두 가지가 난다 —
                       * ① 사람이 그것을 채워 [저장]하면 트레이스가 통째로 갈린다(경고도 확인창도 없다).
                       * ② 체크박스 하나만 눌러도 draft 가 생겨 specDirty 가 참이 되고, 그러면
                       *    [글자 저장]이 「먼저 [저장]을 눌러 주세요」로 잠기는데 그 [저장]도
                       *    잠겨 있어 **창을 닫기 전에는 빠져나올 수 없다**(교착).
                       * 이제 그 그림에서는 이 탭이 서지 않는다(figureEditTabs 의 canSpec) — 잠긴 빈 폼을 보여 주고
                       * 「채우면 된다」로 읽히게 두는 것보다, 할 수 있는 일만 내는 편이 짧다.
                       */
                      <FigSpecForm spec={draft} onChange={setDraft}
                        /* 사진·판 그림으로 바꾸기를 고른 동안은 고칠 값이 없다 — 열어 두면 고친 값이
                           저장되리라 읽히는데, 나가는 것은 `spec:null`(또는 restore)이라 여기 값이 아니다. */
                        disabled={!writable || anyBusy || pickedOriginal || pickedCrop || pickedContentVer != null} />
                    )}
                  </div>
                </Pane>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {/* 지우기 창. 3단 창과 **같은 좌측 칸**을 쓴다 — 한 자리만 고치면 둘 다 낫는다.
                    종전에는 빈칸 문구만 따로여서('보여 줄 그림이 없습니다.'), OCR 에서 페이지
                    이미지가 만료된 경우에도 그 이유를 말하지 못했다. */}
                <Pane label={left.label} height="h-56">
                  {left.src
                    ? <img src={left.src} alt={left.label} className="size-full object-contain" />
                    : <Empty text={left.text} />}
                </Pane>
                <Pane label="지금 본문에 실린 그림" height="h-56">
                  {item.svg || item.src
                    ? <img src={item.svg || item.src} alt={item.alt || '지금 그림'} className="size-full object-contain" />
                    : <Empty text="—" />}
                </Pane>
              </div>
            )}

            {/**
              * 고른 것을 보여 주고 무를 수 있게 한다 — 고르기는 서버에 나가지 않으므로,
              * 무엇을 고른 상태인지 화면이 말하지 않으면 [저장]이 무엇을 보낼지 알 수 없다.
              *
              * ⚠ **좌표·식을 가져온 경우에도 낸다**(2026-09-01). 종전 조건은 `pickedOriginal` 뿐이라
              * 이력에서 [이 버전 가져오기]로 명세를 불러오면 **무르는 버튼이 화면에 하나도 없었다.**
              * 하필 그 상태가 트레이스 그림에서 [글자 저장]을 잠그고(specDirty), 잠긴 사유는
              * 「먼저 [저장]을 누르라」고 하는데 그 [저장]은 트레이스를 좌표·식으로 갈아 없앤다.
              * 되돌아올 문을 만들지 않으면 화면이 파괴 경로를 처방하게 된다.
              */}
            {(pickedOriginal || pickedCrop || loadedVer != null) && (
              <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-2.5">
                <p className="text-[11px] leading-relaxed text-amber-800">
                  {pickedCrop
                    ? '교재 크롭으로 바꾸기를 골랐습니다 — [저장]을 누르면 이 그림이 OCR 당시 지면에서 잘라낸 사진이 됩니다.'
                    : pickedOriginal
                    ? (loadedVer != null
                      ? `이력 v${loadedVer} 은 사진입니다 — [저장]을 누르면 좌표·식을 버리고 이 사진이 됩니다.`
                      : '사진으로 바꾸기를 골랐습니다 — [저장]을 누르면 좌표·식을 버리고 이 사진이 됩니다.')
                    : pickedContentVer != null
                      /* 사진(보관 원본) 회귀와 말이 갈린다 — 이쪽은 그 판의 내용(글자 고침 포함)
                         그대로다(계약 「판 내용 스냅샷」 restore). 같은 문구로 뭉개면 한쪽에 거짓이 된다. */
                      ? `이력 v${pickedContentVer} 의 그림으로 바꾸기를 골랐습니다 — [저장]을 누르면 그 판의 그림(글자 고침 포함) 그대로 돌아갑니다.`
                      : `이력 v${loadedVer} 의 좌표·식을 가져왔습니다 — [저장]을 누르면 지금 그림이 그 값으로 바뀝니다.`}
                  {' '}아직 아무것도 서버에 나가지 않았습니다.
                </p>
                <Button size="xs" variant="secondary" className="ml-auto" disabled={anyBusy} onClick={cancelPick}>
                  {pickedOriginal || pickedCrop || pickedContentVer != null ? '고르기 취소' : '가져오기 취소'}
                </Button>
              </div>
            )}

            {/* 동작 줄 */}
            <div className="flex flex-wrap items-center gap-2">
              {/* 지우기 창에서는 다른 쓰기를 내지 않는다 — 지우러 들어온 창에서 저장·재요청이
                  함께 열려 있으면, 어느 쪽이 나갔는지 사용자가 헷갈린다. */}
              {faces.editPanes && (
                <Button size="xs" variant="secondary" icon={<Wand2 className="size-3.5" />}
                  /* 보관 원본이 없어도 **막지 않는다** — 재요청 창이 화면의 그림을 구워 계약 §4-3 의
                     1순위(body.image)를 채울 수 있다. 종전에는 여기서 잠갔고, 그래서 OCR 에서
                     도판으로 승격된 그림은 다시 그릴 길이 아예 없었다(오너 보고 2026-08-30).
                     2026-09-01 정정: 한때 「은행은 늘 그것을 싣는다」고 적혀 있었는데, 같은 날
                     기본 재료가 보관 원본(`use_original`)으로 뒤집혀 더는 사실이 아니다 —
                     무엇이 나가는지는 창에서 사람이 고른다(FigureReconvertModal 의 defaultPick). */
                  /* 이력을 **보는 중에는 잠근다**(2026-09-01). 요청에 실리는 것은 서버의 지금
                     그림인데 화면은 고른 옛 버전을 그리고 있어, 그대로 두면 「보고 있는 그림을
                     보냅니다」라는 이 창의 약속이 깨진다. [저장]이 같은 이유로 이미 그렇게 한다
                     (canSave 의 previewVer == null). 사진으로 바꾸기를 골라 둔 상태도 같다 —
                     그때 화면이 그리는 것은 아직 저장되지 않은 사진이다. */
                  loading={busyReconvert}
                  disabled={!writable || anyBusy || previewVer != null || pickedOriginal || pickedCrop || pickedContentVer != null}
                  title={previewVer != null
                    /* 2026-09-01: 「먼저 골라서 [저장]하라」가 더는 유일한 길이 아니다 — 요청 창이
                       지난 버전을 카드로 내므로, 저장하지 않고도 그 버전을 재료로 보낼 수 있다. */
                    ? `지금은 이력 v${previewVer} 을 보는 중입니다 — 목록의 [보는 중]을 다시 눌러 끄면 요청할 수 있습니다.`
                      + ` 그 버전을 재료로 보내려면 요청 창에서 [이력 v${previewVer}] 카드를 고르세요.`
                    : pickedOriginal
                      ? '사진으로 바꾸기를 골라 둔 상태입니다 — [저장]으로 반영한 뒤에 요청하면 그 사진을 기준으로 그립니다.'
                      : pickedContentVer != null
                        /* 같은 약속(「보고 있는 그림=보내는 그림」)의 세 번째 갈래 — 화면은 골라 둔
                           판을 그리는데 요청은 서버의 지금 그림을 재료로 잡는다. */
                        ? `이력 v${pickedContentVer} 의 그림으로 바꾸기를 골라 둔 상태입니다 — [저장]으로 반영한 뒤에 요청하거나, [고르기 취소]로 무르세요.`
                        : 'AI 에 그림을 다시 그려 달라고 요청합니다 — 무엇을 재료로 보낼지는 창에서 고릅니다(기본은 보관 원본)'}
                  onClick={() => { setAiMode('faithful'); setAiOpen(true); }}>
                  AI 요청
                </Button>
              )}
              {/* [원래 사진으로]는 아래 「이미지 교체」 칸의 원본 카드로 옮겼다(2026-08-31) —
                  무엇으로 바꿀지 고르는 일은 한자리에 모은다. 원본도 지난 버전도 **고르는 것**이다.
                  지우기 창에서는 종전대로 **이력만** 편다(이름도 [이력] 그대로) — 지우러 온 창에
                  되돌리기·저장을 함께 열어 두지 않는다는 규칙은 그대로다. */}
              {!FIGURE_SVG_EDITOR && <Button size="xs" variant="secondary"
                icon={faces.picksImage ? <Images className="size-3.5" /> : <History className="size-3.5" />}
                disabled={anyBusy} onClick={() => setHistOpen((v) => !v)}>
                {faces.picksImage
                  ? (histOpen ? '이미지 교체 접기' : '이미지 교체')
                  : (histOpen ? '이력 접기' : '이력')}
              </Button>}
              <span className="ml-auto" />
              {busyReconvert && <span className="text-[11px] text-gray-500">그림을 다시 그리고 있습니다 · 취소할 수 없습니다</span>}
              {/* 창이 하나가 된 뒤로는 지우기 말고 **늘 낸다**(2026-08-31). 종전 조건
                  `action === 'edit' || loadedVer != null` 은 「편집 창이거나 이력에서 값을 가져왔을 때」
                  였는데, 가져와도 렌더가 안 돌아 잠긴 채였다(위 useSpecRender 주석). */}
              {faces.saves && (
                <Button size="sm" variant="primary" icon={<Save className="size-3.5" />}
                  /* `drop` 도 이 버튼이 낸다 — 사진으로 바꾸기는 다른 저장이 아니라 **같은 저장이
                     보내는 다른 값**이다(`spec:null`). 버튼을 둘로 내면 「저장하지 않아도 반영되는
                     자리」가 다시 생긴다. */
                  loading={busy === 'save' || busy === 'drop' || busy === 'restore'} disabled={!canSave}
                  title={canSave
                    ? (pickedCrop ? '좌표·식을 버리고 OCR 당시 지면에서 잘라낸 사진으로 저장합니다 — 이력에 버전이 하나 남습니다'
                      : pickedOriginal ? '좌표·식을 버리고 이 사진으로 저장합니다'
                        : pickedContentVer != null ? `이력 v${pickedContentVer} 의 그림 그대로 되돌립니다 — 이력에 버전이 하나 남습니다`
                          : '이 명세로 저장합니다')
                    : (pickedOriginal ? figureDropSpecHint(item) : '그릴 수 있는 값이 되고 설명이 채워져야 저장할 수 있습니다')}
                  /* 네 갈래가 다 이 버튼이다 — 교재 크롭(`PATCH {image}`)·사진(`PATCH {spec:null}`)·판 그림(restore 1회)·
                     명세(`PATCH {spec}`). 버튼을 가르면 「저장하지 않아도 반영되는 자리」가 다시 생긴다. */
                  onClick={() => (pickedCrop ? doReplaceWithCrop()
                    : pickedOriginal ? doSave(null)
                      : pickedContentVer != null ? doRestore()
                        : doSave(pruned, 'edit'))}>
                  저장
                </Button>
              )}
              {/* 삭제에는 item 조건이 없다(계약 §8 — kind 제약 없음). 잠금은 이미 있는 두 축이
                  전부 건다: writable(순번 대조·로드 오류)과 호스트의 busy 게이트(OCR 승격·프로젝트
                  종료면 Provider 를 안 줘 버튼 자체가 안 뜬다). 새 판정을 만들지 않는다. */}
              {deleting && (
                <Button size="sm" variant="danger" icon={<Trash2 className="size-3.5" />}
                  loading={busyDelete} disabled={!writable || anyBusy}
                  /* 잠긴 사유를 갈라 댄다 — 서버가 거절한 것을 「대조가 안 끝났다」고 하면 거짓이고,
                     사용자를 대조를 맞추는 헛일로 보낸다. 사유 자체는 위 토스트가 말한다. */
                  title={writable ? '이 그림을 본문에서 지웁니다 — 되돌릴 수 없습니다'
                    : match?.fromError ? '서버가 이 요청을 거절했습니다 — 위 안내를 보세요'
                      : '서버 그림과 대조가 끝나야 지울 수 있습니다'}
                  onClick={() => setDelOpen(true)}>
                  삭제
                </Button>
              )}
            </div>

            {/* 「그림 속 글자」는 이제 위 고치는 칸의 한 탭이다(2026-09-01, 오너 지시 「합쳐 줄래」).
                여기 따로 서 있던 상자를 걷었다 — 그 상자는 얼굴을 가르는 삼항 **밖**이라
                `faces.editPanes` 를 손으로 한 번 더 걸어야 했는데, 칸 안으로 들어가며 그 조건이
                구조로 보장된다(지우기 창에는 고치는 칸 자체가 없다). */}

            {faces.saves && !canSave && !anyBusy && writable && (
              <p className="text-[11px] text-gray-400">
                {/* 가리키는 자리가 바뀌었다 — 상자를 걷었으므로(2026-09-01) 버튼은 목록 줄에 있고,
                    보기를 끄는 것도 그 줄의 [보는 중]이다. 없는 것을 가리키면 죽은 안내가 된다. */}
                {previewVer != null ? `이력 v${previewVer} 을 보는 중입니다 — 아래 목록에서 그 줄의 버튼으로 바꾸고, 고치던 값으로 돌아가려면 [보는 중]을 다시 누르세요.`
                  : pickedOriginal ? figureDropSpecHint(item)
                  /* 사진을 따라 그린 그림에서는 [저장]이 보낼 것이 없다 — 그런데 종전에는 아래
                     `!altOk` 갈래가 먼저 걸려 「그림 설명을 채워야…」라고 말했고, 그 말을 따르면
                     빈 명세를 채워 저장하는 길(트레이스가 갈리는 길)로 간다. 사유를 먼저 가른다.
                     문구는 「숫자·식」 칸의 것을 되쓰지 않는다 — 그 칸이 바로 위에 같은 문단을
                     이미 그리고 있어, 여기 한 번 더 찍으면 한 화면에 같은 말이 두 번 뜬다. */
                  : traceNoSpec ? '사진을 따라 그린 그림이라 [저장]으로 보낼 좌표·식이 없습니다 — [그림 속 글자] 칸의 [글자 저장]을 쓰세요.'
                  : !altOk ? '그림 설명(한 문장)을 채워야 저장할 수 있습니다.'
                  /* 이름을 손으로 적지 않는다 — §9-1 로 원소가 둘 늘었을 때 이 줄만 낡아 갈렸다 */
                  : !hasDrawable(pruned) ? `그릴 원소(${DRAWABLE_NAMES})가 하나는 있어야 합니다 — 글자만으로는 그릴 수 없습니다.`
                    : rendering ? '미리보기를 그리는 중입니다…'
                      : '미리보기가 통과해야 저장할 수 있습니다.'}
              </p>
            )}

            {!FIGURE_SVG_EDITOR && histOpen && (
              <div className="rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-1.5">
                  <span className="text-xs font-bold text-gray-700">{faces.picksImage ? '이미지 교체' : '이력'}</span>
                  <span className="text-[11px] text-gray-400">
                    {faces.picksImage ? '원본 사진이나 지난 버전을 골라 이 자리의 그림을 바꿉니다' : '이 그림이 지나온 버전입니다'}
                  </span>
                  <Button size="xs" variant="secondary" className="ml-auto" icon={<RefreshCw className="size-3" />}
                    disabled={hist.loading || anyBusy} onClick={loadHistory}>새로 읽기</Button>
                </div>

                {/**
                  * ── 원본 사진 (계약 §4-2 `spec:null`) ──
                  * 지난 버전과 **다른 갈래**다: 이력은 그 버전의 좌표·식을 폼으로 가져와 [저장]으로 나가고,
                  * 이쪽은 좌표·식을 통째로 버려 **서버가 보관한 원본 픽셀**로 돌아간다(그래서 [저장]을
                  * 거치지 않고 바로 나간다). 그런데도 한 칸에 둔 이유는 사람이 하는 일이 같기 때문이다 —
                  * 「이 자리의 그림을 무엇으로 바꿀까」. 종전에는 이것만 동작 줄의 [원래 사진으로]로
                  * 떨어져 있어, 고르는 일이 두 자리로 갈려 있었다.
                  *
                  * 이름은 좌측 칸과 **같은 함수**가 짓는다(figureOriginalPane) — 보관 원본이 없어
                  * 지금 그림을 그리고 있을 때 그것을 「원본」이라 부르면 거짓이 된다.
                  */}
                {faces.picksImage && (
                <div className="flex items-start gap-3 border-b border-gray-100 p-3">
                  <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white">
                    {left.src
                      ? <img src={left.src} alt={left.label} className="size-full object-contain" />
                      : <span className="px-1 text-center text-[10px] text-gray-400">없음</span>}
                  </div>
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <p className="text-xs font-bold text-gray-700">{left.label}</p>
                    <p className="text-[11px] leading-relaxed text-gray-500">
                      {!left.src ? left.text
                        /* 칸이 교재 크롭을 그리고 있으면 이 카드가 곧 [교재 크롭으로 바꾸기]다(`PATCH {image}` · 이력 950) —
                           spec:null 은 보관 원본으로 가므로 등록·교체 뒤에는 이 크롭이 아니다(2026-09-06 · 감사 G1). */
                        : paneIsCrop
                          ? (cropPickable
                            ? '이 자리 그림을 OCR 당시 지면에서 잘라낸 사진으로 바꿉니다 — [저장]을 눌러야 나가고, 이력에 버전이 하나 남습니다.'
                            : cropAlready ? FIGURE_CROP_UNCHANGED_HINT : '이 크롭은 서버가 받는 규격(png·jpeg·webp · 2MB)이 아니라 보낼 수 없습니다.')
                          : caps.canDropSpec
                            ? '좌표·식을 버리고 이 사진으로 바꿉니다 — [저장]을 눌러야 나가고, 그때 이력에 버전이 하나 남습니다.'
                            /* 잠긴 사유가 둘이라 문구도 둘이다 — 명세가 없는 픽셀 그림에 「원본이 없다」고
                               말하면 그 그림이 곧 원본인데 거짓이 된다(figureDropSpecHint). */
                            : figureDropSpecHint(item)}
                    </p>
                    {/* 그림은 그렸지만 **그것이 원본은 아닐 때** — 그 사실을 여기서 말한다
                        (오너 지시 2026-09-01). 위 칸은 지금 본문의 그림을 그리고 이름도 그렇게
                        부르지만, 「그럼 원본은 어디 갔나」에는 아무도 답하지 않고 있었다. */}
                    {left.src && left.state !== 'ok' && left.text && (
                      <p className="text-[11px] leading-relaxed text-amber-700">{left.text}</p>
                    )}
                    {/* **고르기다. 서버에 나가지 않는다**(2026-09-01 오너 원칙). 종전 이름은
                        [이 원본으로 되돌리기]였고 누르는 즉시 PATCH 가 나갔다 — 그래서 「무엇이
                        되는지 보고 정하기」가 이 자리에만 없었다. */}
                    {paneIsCrop ? (
                      <Button size="xs" variant={pickedCrop ? 'primary' : 'secondary'} icon={<ImageOff className="size-3" />}
                        disabled={!writable || anyBusy || !cropPickable || pickedCrop}
                        title={!cropPickable ? (cropAlready ? FIGURE_CROP_UNCHANGED_HINT : '서버가 받는 규격이 아닙니다')
                          : pickedCrop ? '이미 골랐습니다 — [저장]을 누르면 반영됩니다'
                            : '교재 크롭으로 바꾸기를 고릅니다 — 서버에는 [저장]을 눌러야 나갑니다'}
                        onClick={pickCrop}>
                        {pickedCrop ? '고름 · 저장 전' : '교재 크롭으로 바꾸기'}
                      </Button>
                    ) : (
                      <Button size="xs" variant={pickedOriginal ? 'primary' : 'secondary'} icon={<ImageOff className="size-3" />}
                        disabled={!writable || anyBusy || !caps.canDropSpec || pickedOriginal}
                        title={!caps.canDropSpec ? figureDropSpecHint(item)
                          : pickedOriginal ? '이미 골랐습니다 — [저장]을 누르면 반영됩니다'
                            : '이 사진으로 바꾸기를 고릅니다 — 서버에는 [저장]을 눌러야 나갑니다'}
                        onClick={() => pickOriginal(null)}>
                        {pickedOriginal ? '고름 · 저장 전' : '이 사진으로 바꾸기'}
                      </Button>
                    )}
                  </div>
                </div>
                )}

                {/**
                  * ── 교재 크롭 (계약 §10-2 G5 `origin_crop` · OCR 은 `crop`, 2026-09-05 → 09-06 두 대상 공통) ──
                  * OCR 당시 지면에서 잘라낸 사진. 위 칸이 보관 원본·픽셀 자체를 그리고 있고 크롭이 그것과 다른 그림일
                  * 때만 따로 낸다(위 칸이 크롭이면 그 칸의 버튼이 이 일을 한다). 고르기는 화면 안의 일이고 [저장]이
                  * `PATCH {image}` 로 내보낸다(이력 950).
                  */}
                {faces.picksImage && !paneIsCrop && cropPickable && (
                <div className="flex items-start gap-3 border-b border-gray-100 p-3">
                  <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <img src={pageCrop} alt={FIGURE_CROP_PANE_LABEL} className="size-full object-contain" />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <p className="text-xs font-bold text-gray-700">{FIGURE_CROP_PANE_LABEL}</p>
                    <p className="text-[11px] leading-relaxed text-gray-500">
                      이 자리 그림을 OCR 당시 지면에서 잘라낸 사진으로 바꿉니다 — [저장]을 눌러야 나가고, 이력에 버전이 하나 남습니다.
                    </p>
                    <Button size="xs" variant={pickedCrop ? 'primary' : 'secondary'} icon={<ImageOff className="size-3" />}
                      disabled={!writable || anyBusy || pickedCrop}
                      title={pickedCrop ? '이미 골랐습니다 — [저장]을 누르면 반영됩니다' : '교재 크롭으로 바꾸기를 고릅니다 — 서버에는 [저장]을 눌러야 나갑니다'}
                      onClick={pickCrop}>
                      {pickedCrop ? '고름 · 저장 전' : '교재 크롭으로 바꾸기'}
                    </Button>
                  </div>
                </div>
                )}

                {faces.picksImage && (
                <div className="border-b border-gray-100 px-3 py-1.5">
                  <span className="text-xs font-bold text-gray-700">지난 버전</span>
                  {/* 갈래를 여기서 미리 말한다 — 줄마다 버튼 이름이 달라지는 이유가 이것이고,
                      그 까닭이 화면 어디에도 없으면 「어떤 줄은 되고 어떤 줄은 안 되는」 목록으로 읽힌다. */}
                  {/* 사진 줄의 그림이 전부 같아 보이는 데는 까닭이 있다 — 그 말을 안 하면
                      화면이 고장 난 것으로 읽힌다(계약 §4-2: 보관은 픽셀→SVG 첫 전환 때 한 번).
                      단 has_content 판이 하나라도 있으면 그 문장이 거짓이 된다(그 판들은 제각각
                      제 그림으로 돌아간다 · 계약 「판 내용 스냅샷」) — 그때는 내지 않는다. */}
                  <span className="ml-2 text-[11px] text-gray-400">
                    보고 고른 뒤 [저장]을 눌러야 반영됩니다 — 고르기만으로는 아무것도 나가지 않습니다
                    {!anyContentVer && ' · 사진 버전은 모두 서버가 보관한 원본 한 장으로 돌아가서 그림이 같습니다'}
                  </span>
                </div>
                )}

                {/**
                  * 고른 버전을 **여기서 다시 크게 보여 주지 않는다**(오너 지시 2026-09-01
                  * 「이 요소는 필요 없지? 어차피 위에 보이므로」).
                  *
                  * 2026-08-30 에 이 상자를 여기 둔 까닭은 「[보기]를 눌러도 결과가 화면 밖이라
                  * 아무 일도 안 일어난 것처럼 보인다」였다. 그 문제는 이제 **화면을 결과로 데려가는
                  * 것**으로 푼다(previewVer effect → editAreaRef). 상자를 남기면 위 두 칸과 같은
                  * 그림·같은 버튼이 한 창에 두 벌이 되고, 그러면 어느 쪽이 진짜인지가 흐려진다.
                  *
                  * 여기 있던 버튼은 모두 **목록 줄에 이미 있다** — [이 버전 가져오기]·[이 사진으로
                  * 바꾸기]는 줄마다 나오고, [그만 보기]는 [보는 중] 토글이 한다. 잃은 길은 없다.
                  */}
                <FigureHistoryList
                  versions={hist.versions} currentVersion={hist.current}
                  loading={hist.loading} error={hist.error} onRetry={loadHistory}
                  /* 쓰는 중에는 목록의 버튼도 잠근다. 종전 값은 `typeof busy === 'number' ? busy : null`
                     이었는데 `busy` 가 숫자였던 것은 restore 를 쓰던 때뿐이라(2026-08-30 폐기)
                     **늘 null 이었다** — 이름만 남고 아무것도 안 잠그는 죽은 값이었다. */
                  busy={anyBusy}
                  /* 창이 읽기전용이면 loadVersion 이 곧바로 되돌아 나간다 — 그 사실을 버튼에 전한다.
                     안 전하면 눌리는데 아무 일도 없는 죽은 버튼이 된다(FigureHistoryList.canLoad). */
                  canLoad={writable}
                  onLoad={loadVersion}
                  /* 사진 버전이 고르는 것은 **보관 원본 한 장**이다(계약 §4-2) — 그림도 그것을
                     그리고, 저장도 그것으로 간다. 둘을 갈라 넘기면 「보이는 것과 저장될 것」이
                     어긋날 자리가 생긴다. */
                  onPickOriginal={pickOriginal}
                  canPickOriginal={writable && caps.canDropSpec}
                  originalSrc={originalPickSrc}
                  originalHint={figureDropSpecHint(item)}
                  /* 내용이 남은 판(has_content)의 고르기 — [저장]이 restore 를 1회 보낸다(doRestore).
                     사진 줄(onPickOriginal)과 다른 곳으로 돌아가므로 길을 가른다. */
                  onPickContent={pickContentVersion}
                  thumbs={thumbs}
                  /* [보기]가 받아 둔 판 그림 캐시 — 있으면 그 줄의 작은 그림으로도 쓴다.
                     목록을 열 때 미리 받지는 않는다(lazy · 계약 「src 가 큼」). */
                  verSrc={verSrc}
                  loadedVersion={loadedVer}
                  previewVersion={previewVer}
                  onPreview={setPreviewVer}
                />
              </div>
            )}
            </>)}
          </>
        )}
      </div>

      {/* 이 모달(--z-modal-figure 9999985) 위에 뜬다(--z-modal-ai-run 9999986).
          위의 closeOnEsc 에 !aiOpen 을 건 것과 짝이다 — 안 걸면 ESC 한 번에 둘 다 닫힌다. */}
      <FigureReconvertModal
        isOpen={aiOpen}
        target={target}
        /* **화면에 보이는 그 그림**을 넘긴다 — 창이 이것을 구워 계약 §4-3 의 1순위(body.image)를
           사람 손 없이 채운다(오너 지적 2026-08-30 「그림을 보고 눌렀는데 원본을 올리라니?」).
           2026-08-31 부터 은행은 **늘** 싣는다(오너 지시) — 종전에는 보관 원본이 없다고 확인된
           그림에서만 보냈고, 그 판정이 틀리면 409 `no_original` 로 돌아왔다.
           표시 규칙과 같은 순서로 고른다(계약 §1-2: `svg ?? src`) — 화면이 그리는 것과 보내는 것이
           다르면 「이 그림을 보낸다」는 말이 거짓이 된다. */
        screenSrc={nowSrc}
        /* 크기는 **숫자 둘로 쪼개 넘긴다.** 객체로 주면 이 창이 리렌더될 때마다 새 정체성이 되어
           재요청 창의 「열 때마다 비운다」 effect 가 다시 돌고, 사람이 쓰던 지시가 지워진다.
           이 창은 미리보기 렌더 응답마다 리렌더되므로 실제로 일어난다. */
        screenWidth={Number.isFinite(sendNow.width) ? sendNow.width : null}
        screenHeight={Number.isFinite(sendNow.height) ? sendNow.height : null}
        /* 변환 전 원본 사진(계약 §1-2 `original_src`) — 화면 그림과 다를 때만 후보로 뜬다.
           도판을 다시 보내면 AI 는 **한 번 옮겨진 결과**를 옮기게 되므로, 원본이 남아 있으면
           고를 수 있어야 한다. 무엇을 보낼지는 창이 정하지 않고 사람이 고른다.
           **이 값은 보기용이다** — 고르면 나가는 것은 `use_original:true` 이고, 서버가 제 보관본을
           쓴다(계약 §9-4, 2026-09-01). 그래서 그림을 못 받았어도(`has_original` 만 참) 고를 수 있다. */
        /* 보관 원본(`original_src`) — 2026-09-06 회신 B 로 **두 대상 모두** 실려 온다(dev `92c98ef`).
           고르면 `use_original:true` 로 나가 서버가 제 보관본을 본다(회신 C). 페이지 크롭은 아래 originCropSrc 다.
           종전에는 OCR 에 크롭을 이 자리에 넣어, 교체(950)한 그림에서 카드가 보여 주는 것(크롭)과 서버가 보는 것
           (올린 이미지)이 갈렸다(감사 G2) — 그 갈래가 사라졌다. */
        originalSrc={item?.original_src || null}
        /* 교재 크롭(OCR 당시 지면 · figurePageCrop) — 보관 원본과 다르면 [교재 크롭] 카드가 되고 그 바이트가 image 로 나간다.
           2026-09-06 부터 두 대상이 같다. 규격 밖이면 카드를 내지 않는다(보낼 수 없는 카드를 내지 않는다). */
        originCropSrc={cropSendable ? pageCrop : null}
        /* 원본이 **지금 어떤 상태인가** — 판정을 창 안에서 다시 짓지 않고 그대로 넘긴다
           (figureText.figureOriginalPane). 둘로 갈라 두면 카드는 뜨는데 이유는 다른 말을 하거나,
           한쪽만 고쳐져 어긋난다. `serverHas` 가 거짓일 때만 [원본 사진]이 확실히 실패한다. */
        originalKept={left.serverHas}
        originalText={left.text}
        /* **지난 버전도 고른다**(2026-09-01 오너 지시). 그림은 이 창이 이미 그려 둔 이력
           썸네일을 그대로 쓴다 — 같은 버전을 두 번 그리지 않는다. 무엇을 거르는지는 aiVersions. */
        versions={FIGURE_SVG_EDITOR ? [] : aiVersions}
        /* 카드를 골랐는데 그 판의 그림이 아직 없을 때 — 그때 1건만 받는다(lazy · 계약 「src 가 큼」). */
        onNeedVersion={setAiWantVer}
        versionsLoading={hist.loading}
        /* 거절 뒤 [사진 따라 그리기로 해보기] 로 열면 그 모드가 골라진 채로 뜬다 — 사람이
           방금 고른 길을 창이 다시 기본값으로 되돌리면 같은 거절을 한 번 더 맞는다. */
        initialMode={aiMode}
        /* 사진을 따라 그린 그림인가 — 그때 이 창이 다른 말을 한다(그쪽 prop 주석).
           판정의 출처는 하나다: 그림 안의 글자 목록(traceIds). 창이 따로 재지 않는다. */
        traced={!FIGURE_SVG_EDITOR && traceIds.length > 0}
        busy={busyReconvert}
        onConfirm={(payload) => { setAiOpen(false); setAiWantVer(null); doReconvert(payload); }}
        onClose={() => { setAiOpen(false); setAiWantVer(null); }}
      />

      {/* 삭제 확인 창 — AiRunModal 과 같은 층(--z-modal-ai-run 9999986)을 쓴다. 둘은 action 이
          갈라 동시에 뜨지 않는다. 위 closeOnEsc 의 !delOpen 과 짝이다. */}
      <FigureDeleteModal
        isOpen={delOpen}
        target={target}
        field={field}
        lexicalEdited={lexicalEdited === true}
        editedAtRisk={deleteDropsEdited}
        busy={busyDelete}
        onConfirm={doDelete}
        onClose={() => setDelOpen(false)}
      />
    </Modal>
  );
}

// height: 칸 높이를 못 박는 Tailwind 클래스(예: 'h-72'). 주면 안의 그림이 바뀌어도 창이
// 들썩이지 않는다 — 서버는 명세에 따라 도판 크기를 다르게 내므로, 손잡이를 옮길 때마다
// 칸이 늘었다 줄었다 하며 깜박였다. 안 주면 종전대로 내용에 맞춰 늘어난다.
function Pane({ label, busy, right, height, children }) {
  return (
    <div className="min-w-0 rounded-xl border border-gray-200">
      <div className="flex items-center gap-1.5 border-b border-gray-100 px-2.5 py-1.5 text-[11px] text-gray-500">
        {/* 머리줄이 길어질 수 있다(손으로 옮기기가 막힌 까닭이 여기 선다 · 2026-09-06) — 자르지 않으면
            오른쪽 [손으로 옮기기]·눈금 칸이 화면 밖으로 밀린다. 전문은 title 로 준다(그쪽에서 붙인다). */}
        <span className="min-w-0 truncate">{label}</span>
        {busy && <span className="size-1.5 rounded-full bg-primary motion-safe:animate-pulse" aria-hidden="true" />}
        {right && <span className="ml-auto">{right}</span>}
      </div>
      <div className={`flex items-center justify-center overflow-auto p-2 ${height || 'min-h-32'}`}>
        {children}
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <p className="p-3 text-center text-[11px] text-gray-400">{text}</p>;
}
